/* eslint-disable no-nested-ternary */
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
// mui
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Popover, Snackbar, Tooltip, Typography, MenuItem, Popper, Grow, Paper, MenuList, Zoom, ClickAwayListener, useTheme, useMediaQuery, Skeleton } from '@mui/material';
// other libraries
import moment from 'moment';
import { isArray } from 'lodash';
import { useNavigate } from 'react-router';
// components
import { fetchMergeTagValueForLeads, fetchMergeTagValueForTreatments } from 'src/redux/slices/mergeTags';
import { updatePracticeIdToChange } from 'src/redux/slices/practiceTreatmentList';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import useAuth from 'src/hooks/useAuth';
import useGoogleReviewSend from 'src/hooks/useGoogleReviewSend';
import { Iconify } from '../../../../components';
import { DataGridTableMoreMenu } from '../../../../components/table';
import MobileLeadCard from './MobileLeadCard';
import ContactCell from './CotactCell';
import BookedWithField from './BookedWithField';
import ChoicesField from './ChoicesField';
import CustomTreatmentNameCell from './CustomTreatmentNameCell';
import CustomTreatmentPriceCell from './CustomTreatmentPriceCell';
import CustomTreatmentPriceEditCell from './CustomTreatmentPriceEditCell';
import CustomViewDetailComponent from './CustomViewDetailComponent';
import CustomArchivingButton from './CustomArchivingButton';
import CustomRecordAttendanceBtn from './CustomRecordAttendanceBtn';
import CustomFollowUpBtn from './CustomFollowUpBtn';
import CustomCompleteTreatmentBtn from './CustomCompleteTreatmentBtn';
import CustomFollowUpBtnV2 from './CustomFollowUpBtnV2';
import CustomSelectTreatmentCell from './CustomSelectTreatmentCell';
import GetDetailPanelContent from './GetDetailPanelContent';
import CustomDepositPaymentCell from './CustomDepositPaymentCell';
import { ApptMethodCell, ApptTypeCell, AssigneeCell, AttendedDateCell, AttendedTableColumnActions, BookedWithCell, ClincheckStatusCell, ConsultationBookedWithCell, ConsultationDateCell, ContactActionCell, CreatedAtDateCell, DateOfAppointmentCell, DepositPendingCell, EmailCell, FirstNameCell, LastContactCell, LastNameCell, LeadNameCell, ReferralCell, StartedTableColumnActions, StatusCell, TagCell, TransitionDateCell, TreatmentPriceCell, ViewDetailCell } from './tableCells';
import DataGridSlots from './DataGridSlots';
import { ArchivedLeadsColumns } from './tableColumns';
import useTaskChecklistColumns from '../../../../hooks/useTaskChecklistColumns';
import TaskChecklistField from './TaskChecklistField';
import { CompleteTreatmentMenuItem, MarkAsSpamMenuItem, StartTreatmentMenuItem } from './tableMenuItems';
// redux
import { setCurrentTreatmentDataGlobal } from '../../../../redux/slices/multiTreatment';
import { followUpSlice, leadPage, tableLead } from '../../../../redux/slices/lead';
import { updateError } from '../../../../redux/slices/user';
import { dispatch, useSelector } from '../../../../redux/store';
import { callApi } from '../../../../utils/apiCall';
import { selectTablePreferencesForGrid, selectLeadLoadingStates, selectChoicesData } from '../../../../redux/selectors/dataGridSelectors';
// utils
import { getTextColor, getTreatmentCellClassName } from '../../../../utils/getClassName';
import { UpdateTableData } from '../../../../utils/allTableTab';
import { applyTablePreferences, ChangePinnedColumns, getCurrentTableData, handleColumnWidthChange, handleSortingChange, handleTablePreference } from '../../../../utils/tablePreferences';
import { getAssignees } from '../../../../utils/multiAsignees';
import { syncDentallyAppointment } from '../../../../utils/syncDentallyAppointment';
import capitalizeFirstLetter from '../../../../utils/capitalizedFunc';
// hooks/utils/constants
// useResponsive removed — using useMediaQuery for mobile detection instead
import useGetCurrentUserId from '../../../../hooks/useGetCurrentUserId';
import {
    formatData,
    getColumns,
    getRowClassName,
    getTableColumns,
    handleGetVisibleColumns,
    processRowUpdate,
    showDate,
} from '../../../../utils/leadTableUtils';
import { getTransitionDate, handleChangeDateFormat } from '../../../../utils/dataGridUtils';
import { commonColumns } from '../../../../constants/newLead';
import { actionColumnData, initialPaginationData, phoneNumberCell, treatmentFieldColumnData } from '../../../../constants/dataGridConstants';
// style
import { common } from '../../../../theme/palette';
import {
    menuListStyle,
    tableMoreMenuBoxStyle,
    tableCellInnerBoxStyle,
    treatmentNameStyle
} from '../../../../styles/NewLeadTableRowStyle';
import { leadDetailTabsGridStyle } from '../../../../styles/NewLead';
import { treatmentNameCellTypoStyle } from '../../../../styles/LeadTableStyle';

const ACTION_GROUP = ['contact', 'viewDetails', 'actions'];

const NewLeadDataGrid = ({
    tableData,
    typed,
    isReportData,
    selectedRow,
    tableTitle,
    tablePath,
    paginationModel,
    setPaginationModel,
    handlePaginationChange,
    pageCount,
    handleChangePerSize,
    OpenAssignTask,
    reloadCounter,
    updateLeadPage,
    refreshTable,
    refreshCounters,
    removeRowOptimistically,
    restoreRowOptimistically,
    openBulkArchive,
    openBulkReminder,
    onSelectionModelChange,
    forceUpdate,
    sortingApiCall,
    columnVisibilityModel,
    handleColumnVisibilityChange,
    handleColumnOrderChange,
    openAppointmentAssignTaskFuc,
    handleReloadAPI,
    setTableData,
    handleSetTableData,
    setCurrentTreatmentData,
    isMultiTreatmentTable,
    setColumnVisibilityModel,
    pinProps,
    forceLoading,
    showPracticeColumn,
    hasActiveFilters,
    onClearFilters,
    isCombinedName,
    handleToggleCombineName,
    setIsCombinedName,
    isEnforcedByPractice,
    setIsEnforcedByPractice,
    customTabOverride,
    activeSubTab,
    activeCustomTabSlug,
    suppressTaskChecklistColumns,
    onBulkCompleteReady,
    checklistSwitching,
    onChecklistSwitchingDone,
}) => {
    const { slateGray } = common;
    const [selectData, setSelectedData] = useState({});
    const [open, setOpen] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
    // Tracks row IDs mid-animation so getRowClassName can apply CSS classes.
    const [exitingRowIds, setExitingRowIds] = useState(new Set());
    const [enteringRowIds, setEnteringRowIds] = useState(new Set());
    // Keyed by leadId string so concurrent or rapid successive removals don't clobber each other.
    // Fix #3: was a single `useRef(null)` — replaced with Map so each lead gets its own slot.
    const removedRowRef = useRef(new Map()); // Map<string, {row, index}>
    // Tracks how many rows have been optimistically removed but not yet restored/reconciled.
    // Drives the rowCount cap (Fix #1): prevents MUI DataGrid from rendering phantom filler rows
    // when pageCount (server total) > tableData.length (local page after splice).
    const [optimisticRemovedCount, setOptimisticRemovedCount] = useState(0);
    // Holds the ID of the failsafe background-refresh timer started by makeOnChecklistComplete.
    // Cancelled on undo (Fix #2) so a refetch at 5 s cannot stomp an undo pressed at 5–6 s.
    const failsafeTimerRef = useRef(null);
    // Confirmation dialog for reversing a complete lead back to in_progress.
    const [reopenConfirm, setReopenConfirm] = useState(null); // { leadName: string } | null
    const reopenConfirmResolverRef = useRef(null);

    const practiceId = useSelector((state) => state.practiceTreatmentList.practiceId);
    const { results, tablePreferenceLoading, globalTablePreferences, practiceTablePreferences } = useSelector(selectTablePreferencesForGrid);
    const { archivedLoading, loadingForLead, loadingForClincheckLead } = useSelector(selectLeadLoadingStates);
    const choicesData = useSelector(selectChoicesData);
    const theme = useTheme();
    const dateFormat = useSelector((state) => state.lead.dateFormat);
    const dateArchiveFormat = useSelector((state) => state.lead.dateArchiveFormat);
    const dateAttendedFormat = useSelector((state) => state.lead.dateAttendedFormat);
    const dateAppointmentFormat = useSelector((state) => state.lead.dateAppointmentFormat);
    const consultationDateFormat = useSelector((state) => state.lead.consultationDateFormat);
    const dateLastContactFormat = useSelector((state) => state.lead.dateLastContactFormat);
    const reasons = useSelector((state) => state.schema.reasons);

    const checklistColumnInsertion = useMemo(() => {
        const practiceResults = practiceTablePreferences?.results || [];
        const pref = practiceResults.find((r) => r.table_name === tableTitle);
        return pref?.checklist_column_insertion || 'after_lead_name';
    }, [practiceTablePreferences, tableTitle]);

    const isMobile = useMediaQuery('(max-width:768px)');
    const [mobileViewMode, setMobileViewMode] = React.useState(() => {
        try { return localStorage.getItem('mobileTableView') || 'cards'; } catch { return 'cards'; }
    });

    const handleMobileViewToggle = (mode) => {
        setMobileViewMode(mode);
        try { localStorage.setItem('mobileTableView', mode); } catch { /* ignore */ }
    };
    const anchorRef = useRef(null);
    const anchorMoreMenuRef = useRef(null);
    const [openMenu, setOpenMenuActions] = useState(null);
    const [menuCurrentTarget, setMenuCurrentTarget] = useState(null);
    const [sortModel, setSortModel] = useState([]);
    const [syncingLeadIds, setSyncingLeadIds] = useState(new Set());
    const apiRef = useGridApiRef();
    const { enqueueSnackbar } = useCustomSnackbar();

    // ── Google Review row action ─────────────────────────────────────────────
    const { user } = useAuth();
    const practiceUserResults = useSelector((state) => state.practiceUser.practiceUser?.results);
    const { sendRequest: sendGoogleReviewRequest, hasTemplate: hasGoogleReviewTemplate } = useGoogleReviewSend();
    // Fail-closed while practiceUser data loads — same gate as ManageBusiness
    const practiceUserEntry = practiceUserResults?.find(
        (el) => el.user && el.user.id === user?.pk
    );
    const isPractitioner = !practiceUserEntry || practiceUserEntry.role === 'Practitioner';

    /** "Send Google Review Request" menu item — only for CLOSED / TREATMENT_STARTED rows. */
    const googleReviewMenuItem = (params) => {
        if (isPractitioner) return null;
        const { status, lead_id: leadId, id, treatment_id: treatmentId } = params.row;
        if (status !== 'CLOSED' && status !== 'TREATMENT_STARTED') return null;
        return (
            <Tooltip
                key="google-review-request"
                title={!hasGoogleReviewTemplate ? 'No Google review template configured' : ''}
                placement="left"
            >
                <span>
                    <MenuItem
                        disabled={!hasGoogleReviewTemplate}
                        onClick={() => {
                            setOpenMenuActions(null);
                            sendGoogleReviewRequest({
                                leadId: leadId ?? id,
                                leadTreatmentId: treatmentId,
                                leadData: params.row,
                            });
                        }}
                        sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                    >
                        <Iconify
                            style={{ color: '#FFAB00', marginRight: 15 }}
                            width={20}
                            height={20}
                            icon="solar:star-bold-duotone"
                        />
                        Google Review Request
                    </MenuItem>
                </span>
            </Tooltip>
        );
    };

    const handleSorting = useCallback(({ key, order }) => {
        setSortModel([{ field: key, sort: order }]);
        sortingApiCall(key, order);
        UpdateTableData(typed, { order, orderValue: key, perPage: 10, pageNumber: 0 });
        setPaginationModel({ pageSize: 10, page: 0 });
    }, [sortingApiCall, typed, setPaginationModel]);

    useEffect(() => {
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentPracticeTableData = getCurrentTableData(practiceTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const hasAnyPrefs = (isArray(currentTableData) && currentTableData.length > 0) || (isArray(currentPracticeTableData) && currentPracticeTableData.length > 0);
        if (isMultiTreatmentTable || !hasAnyPrefs) return undefined;

        // Mount-race fix: when this effect first fires (right after pref data
        // lands in Redux), `apiRef.current` may not yet be populated by the
        // DataGrid mount. `getAllColumns()` then returns [], `handleTablePreference`
        // builds an empty visibility model, and MUI silently defaults every
        // undeclared column (phone, email, …) back to visible — which the user
        // perceives as their hidden columns reappearing on Reset / first load.
        // Poll up to ~2s for the grid to mount, then apply once.
        let cancelled = false;
        let attempts = 0;
        const MAX_ATTEMPTS = 40; // 40 × 50ms = 2s
        const tryApply = () => {
            if (cancelled) return;
            const allColumns = apiRef?.current?.getAllColumns()?.map((ele) => ele?.field) || [];
            if (allColumns.length === 0) {
                attempts += 1;
                if (attempts < MAX_ATTEMPTS) {
                    setTimeout(tryApply, 50);
                }
                return;
            }
            const dynamicChoiceFields = [];
            choicesData?.forEach((c) => {
                if (c.metadata?.tables?.includes(tableTitle)) dynamicChoiceFields.push(c.name);
            });
            handleTablePreference({ allColumns, setColumnVisibilityModel, tableTitle, setPinnedColumns: pinProps.setPinnedColumns, handleSorting, setIsCombinedName, alwaysVisibleFields: dynamicChoiceFields, userId, setIsEnforcedByPractice, apiRef, customTabOverride });
        };
        tryApply();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results, globalTablePreferences, practiceTablePreferences?.results?.length, choicesData, customTabOverride]);

    const showTransitionDate = useCallback((data, row) => {
        const treatmentData = row?.treatmentData?.find((ele) => ele?.id === row?.treatment_id);
        const transitionDateTime = getTransitionDate(treatmentData?.state_transition_logs || []);
        if (transitionDateTime) {
            const formattedDate = moment(transitionDateTime, 'DD-MM-YYYY HH:mm:ss').format('ddd DD MMM YYYY');
            const formattedTime = moment(transitionDateTime, 'DD-MM-YYYY HH:mm:ss').format('hh:mm A');
            return `${formattedDate} <span>at ${formattedTime}</span>`;
        }
        return '';
    }, []);

    // useEffect(() => {
    //     setTableRefresh(Math.random())
    // }, [tableData]);

    const navigate = useNavigate();

    const handleCurrentTreatmentData = useCallback((row) => {
        const payload = {
            lead_id: Number(row?.id?.toString()?.split("-")?.[0] || '0'),
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            treatmentData: row?.treatmentData?.find((ele) => ele.id === row?.treatment_id)
        };
        setCurrentTreatmentData(payload);
        dispatch(setCurrentTreatmentDataGlobal(payload));
    }, [setCurrentTreatmentData]);

    const setCustomTimeOut = useCallback((row, key, id) => {
        handleCurrentTreatmentData(row);
        dispatch(followUpSlice(id, showPracticeColumn ? row.practiceId : practiceId, undefined, () => {
            selectedRow(row, key);
            dispatch(tableLead(row));
        }));
    }, [handleCurrentTreatmentData, showPracticeColumn, practiceId, selectedRow]);

    const handleAttendedFailedClick = useCallback((id, row) => {
        setCustomTimeOut(row, 'recordUnAttended', id);
    }, [setCustomTimeOut]);

    const handleAttendedClick = useCallback((id, row) => {
        setCustomTimeOut(row, 'recordAttended', id);
    }, [setCustomTimeOut]);

    const handleAttendedPopup = useCallback((id, row) => {
        setCustomTimeOut(row, 'consultationAttended', id);
    }, [setCustomTimeOut]);

    const buttonHandleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        if (anchorMoreMenuRef.current && anchorMoreMenuRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
        setMoreMenuOpen(false);
    };

    const handlePopup = async (id, status, row, openWhatsapp) => {
        if (showPracticeColumn) {
            enqueueSnackbar(`Switching Practice to ${capitalizeFirstLetter(row?.practiceName)}`, {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
            dispatch(updatePracticeIdToChange(row?.practiceId));
            await new Promise(resolve => setTimeout(resolve, 2000));
            enqueueSnackbar(`Practice switched to ${capitalizeFirstLetter(row?.practiceName)}`, {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
        }
        handleCurrentTreatmentData(row);
        Promise.all([
            dispatch(fetchMergeTagValueForLeads(id)),
            dispatch(fetchMergeTagValueForTreatments(id, row?.treatment_id ?? row?.lead_treatments?.[0]?.id ?? null)),
        ]);
        if (status === 'FAILED_TO_ATTEND_CONSULTATION') {
            handleAttendedFailedClick(id, row);
            return;
        }
        dispatch(tableLead(row));
        const extraProps = {
            openWhatsapp: true,
            treatmentCallBack: () => { }
        }
        dispatch(followUpSlice(id, showPracticeColumn ? row.practiceId : practiceId, undefined, () => {
            selectedRow(row, 'contactLead', true, openWhatsapp ? extraProps : null);
        }));
    };

    const handleSyncDentally = async (row) => {
        const leadId = row.lead_id || row.id;
        setSyncingLeadIds(prev => new Set(prev).add(leadId));
        try {
            const appts = row.originalData?.lead_treatments?.[0]?.appointments || [];
            const success = await syncDentallyAppointment({
                practiceId,
                leadId,
                appointments: appts,
                leadMetadata: row.lead_metadata || [],
                enqueueSnackbar,
            });
            if (success && updateLeadPage) updateLeadPage();
        } finally {
            setSyncingLeadIds(prev => {
                const next = new Set(prev);
                next.delete(leadId);
                return next;
            });
        }
    };

    const viewDetailBtn = async (id, status, row) => {
        handleCurrentTreatmentData(row);
        if (status === 'CLOSED') {
            dispatch(leadPage(tableTitle ? { name: tableTitle, path: tablePath } : {}));
            if (showPracticeColumn) {
                navigate(`/dashboard/practice/${row?.practiceId}/lead-detail/${row?.lead_id}`);
                dispatch(updatePracticeIdToChange(row?.practiceId));
                await new Promise(resolve => setTimeout(resolve, 2000));
                enqueueSnackbar(`Practice switched to ${capitalizeFirstLetter(row?.practiceName)}`, {
                    variant: 'success',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    }
                });
            }
            else
                navigate(`/dashboard/practice/${practiceId}/lead-detail/${id}`);
            return;
        }
        if (status === 'TREATMENT_STARTED') {
            selectedRow(row, 'TreatmentConfirmation');
        }
    };

    const handleNavigate = async (row) => {
        if (showPracticeColumn) {
            enqueueSnackbar(`Switching Practice to ${capitalizeFirstLetter(row?.practiceName)}`, {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
            dispatch(updatePracticeIdToChange(row?.practiceId));
            await new Promise(resolve => setTimeout(resolve, 2000));
            enqueueSnackbar(`Practice switched to ${capitalizeFirstLetter(row?.practiceName)}`, {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
        }
        if (typed !== 'ArchiveTable' && !row?.is_archive_pending) {
            dispatch(leadPage(tableTitle ? { name: tableTitle, path: tablePath } : {}));
            if (showPracticeColumn) {
                navigate(`/dashboard/practice/${row?.practiceId}/lead-detail/${row?.lead_id}`);
                dispatch(updatePracticeIdToChange(row?.practiceId));
                await new Promise(resolve => setTimeout(resolve, 2000));
                enqueueSnackbar(`Practice switched to ${capitalizeFirstLetter(row?.practiceName)}`, {
                    variant: 'success',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    }
                });
            }
            else
                navigate(`/dashboard/practice/${practiceId}/lead-detail/${row?.lead_id}`);
        }
    };

    const handleArchivedNavigate = async (row) => {
        if (showPracticeColumn) {
            enqueueSnackbar(`Switching Practice to ${capitalizeFirstLetter(row?.practiceName)}`, {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
            dispatch(updatePracticeIdToChange(row?.practiceId));
            await new Promise(resolve => setTimeout(resolve, 2000));
            enqueueSnackbar(`Practice switched to ${capitalizeFirstLetter(row?.practiceName)}`, {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
        }
        if (typed === 'ArchiveTable' && !row.is_archive_pending) {
            dispatch(leadPage(tableTitle ? { name: tableTitle, path: tablePath } : {}));
            navigate(`/dashboard/practice/${practiceId}/archived/${row?.lead_id}`);
        }
    };

    const handleOpenMenu = useCallback((event, params) => {
        if (!params.is_archive_pending) {
            setOpenMenuActions(params.row.id);
            setMenuCurrentTarget(event.currentTarget);
        }
    }, []);

    const archiveViewDetails = (params) => {
        dispatch(leadPage(tableTitle ? { name: tableTitle, path: tablePath } : {}));
        navigate(`/dashboard/practice/${practiceId}/archived/${params.row.lead_id}`);
    };

    const handleStartTreatment = (row) => {
        setOpenMenuActions(null);
        selectedRow(row, 'StartTreatmentConfirmation');
    };
    const handleCompleteTreatment = (row) => {
        setOpenMenuActions(null);
        selectedRow(row, 'TreatmentConfirmation');
    };

    const handleEditAppointment = (row) => {
        setOpenMenuActions(null);
        selectedRow(row, 'EditAppointment');
    };

    const handleAddAppointment = (row) => {
        setOpenMenuActions(null);
        selectedRow(row, 'AddAppointment');
    };
    const handleDeleteTreatmentPayment = (row) => {
        setOpenMenuActions(null);
        selectedRow(row, 'deleteTreatmentPayment');
    };

    const handleUnMarkAsDuplicate = (row) => {
        setOpenMenuActions(null);
        dispatch(tableLead(row));
        selectedRow(row, 'unMarkAsDuplicate');
    };

    const handleMarkAsSpam = (row) => {
        setOpenMenuActions(null);
        dispatch(tableLead(row));
        selectedRow(row, 'markAsSpam');
    };

    const handleCompletedToStartTreatment = (row) => {
        setOpenMenuActions(null);
        selectedRow(row, 'CompletedToStartTreatment');
    };

    const handleMarkAsDuplicate = (row) => {
        setOpenMenuActions(null);
        dispatch(tableLead(row));
        selectedRow(row, 'markAsDuplicate');
    };

    const handleBacktoAttendedPopup = (id, row) => {
        dispatch(followUpSlice(id, showPracticeColumn ? row.practiceId : practiceId));
        setOpenMenuActions(null);
        dispatch(tableLead(row));
        selectedRow(row, 'backToAttended');
    };

    const handleArchivedDetails = (id, row) => {
        dispatch(followUpSlice(id, showPracticeColumn ? row.practiceId : practiceId));
        setOpenMenuActions(null);
        dispatch(tableLead(row));
        selectedRow(row, 'archivedLead');
    };

    const handleRestore = (row) => {
        setTimeout(() => {
            setOpenMenuActions(null);
        }, 800);
        dispatch(tableLead(row));
        selectedRow(row, 'restoreArchived');
    };

    const handleSelect = useCallback((data) => {
        setSelectedData(data);
        return data;
    }, []);

    const handleNext = useCallback((row, event, openWhatsapp = false) => {
        if (openWhatsapp) {
            if (row?.treatment_id) {
                handleCurrentTreatmentData(row);
                Promise.all([
                    dispatch(fetchMergeTagValueForLeads(row?.id)),
                    dispatch(fetchMergeTagValueForTreatments(row?.id, row?.treatment_id ?? row?.lead_treatments?.[0]?.id ?? null)),
                ]);
            } else {
                dispatch(fetchMergeTagValueForLeads(row?.id));
            }
            dispatch(tableLead(row));
            const extraProps = {
                openWhatsapp: true,
                treatmentCallBack: (row) => {
                    handleCurrentTreatmentData(row);
                    dispatch(fetchMergeTagValueForTreatments(row?.id, row?.treatment_id ?? row?.lead_treatments?.[0]?.id ?? null))
                }
            }
            dispatch(followUpSlice(row?.id, showPracticeColumn ? row.practiceId : practiceId, undefined, () => {
                selectedRow(row, 'contactLead', true, extraProps);
            }));
        } else {
            selectedRow(row, 'addTreatment', '', event);
            dispatch(tableLead(row));
        }
    }, [handleCurrentTreatmentData, showPracticeColumn, practiceId, selectedRow]);

    const defaultValues = {
        create_date: dateFormat,
        date_attended: dateAttendedFormat,
        consultation_date: consultationDateFormat,
        lastContact: dateLastContactFormat,
        'lead_treatments__appointments__datetime': dateAppointmentFormat,
    };

    const methods = useForm({
        defaultValues,
    });

    const { setValue } = methods;

    // Re-sync header dropdowns when Redux format state changes (e.g. after practice loads
    // and seedDateFormatsFromPractice overrides the initial localStorage/fallback value).
    useEffect(() => {
        setValue('create_date', dateFormat);
        setValue('date_attended', dateAttendedFormat);
        setValue('consultation_date', consultationDateFormat);
        setValue('lastContact', dateLastContactFormat);
        setValue('lead_treatments__appointments__datetime', dateAppointmentFormat);
    }, [dateFormat, dateAttendedFormat, consultationDateFormat, dateLastContactFormat, dateAppointmentFormat, setValue]);

    const handleClinCheckStatusShow = (row, event) => {
        selectedRow(row, 'ClinCheckStatusOpen', '', event);
    };

    const handleMoreMenuTogglePopper = (id) => {
        anchorMoreMenuRef.current = document.getElementById(`menuButton-${id}`);
        setMoreMenuOpen((prevStates) => ({
            ...prevStates,
            [id]: !prevStates[id],
        }));
    };

    const isMoreMenuPopperOpen = (id) => Boolean(moreMenuOpen[id]);

    const handleTreatmentClick = (e, row) => {
        if (typed === 'ArchiveTable') {
            handleArchivedNavigate(row);
            return;
        }
        selectedRow(row, 'editTreatment', '', e);
    };

    const handlePracticeChangeClick = (e, row) => {
        selectedRow(row, 'changePractice', '', e);
    }

    // Hide the detail panel toggle button — the expand/collapse is handled by row click,
    // not the built-in MUI toggle. Without this, a "+" icon appears in every row.
    const toggleCell = {
        field: '__detail_panel_toggle__',
        headerName: '',
        width: 0,
        minWidth: 0,
        maxWidth: 0,
        hideable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: () => null,
        renderHeader: () => null,
    };

    const treatmentCell = {
        ...treatmentFieldColumnData,
        cellClassName: (params) => getTreatmentCellClassName(params?.row?.treatmentBgColor),
        renderCell: (params) => {
            if ((params?.row?.treatment_name === '' || null) && typed !== 'ArchiveTable' && isReportData) {
                return (
                    <Box onClick={(e) => handleNext(params?.row, e)} sx={tableCellInnerBoxStyle} disabled={params?.row?.is_archive_pending}>
                        <Button
                            color='primary'
                            variant='outlined'
                            disabled={params?.row?.is_archive_pending}
                        >
                            Select Treatment
                        </Button>
                    </Box>
                );
            }
            return (
                <CustomTreatmentNameCell
                    params={params}
                    handleTreatmentClick={handleTreatmentClick}
                    ref={apiRef}
                />
            );
        },
    };

    const renderPriceEditInputCell = (params) => <CustomTreatmentPriceEditCell params={params} />;
    const renderPriceCell = (params) => <CustomTreatmentPriceCell params={params} />;

    const attendedDateCellProps = {
        methods,
        handleChangeDateFormat,
        typed,
        updateLeadPage,
        reloadCounter,
        openAppointmentAssignTaskFuc,
        setValue,
    };

    const attendedDateCell = {
        ...DateOfAppointmentCell({ ...attendedDateCellProps, label: 'FTA Date' })
    };

    const editAppointmentMenuItem = (params) => {
        if (params.row.status === 'CONSULTATION_BOOKED' && params.row?.appointments?.length > 0) {
            return <MenuItem
                onClick={() => handleEditAppointment(params.row)}
            >
                <Iconify icon={'solar:pen-bold'} />
                Edit appointment
            </MenuItem>;
        }
        return null;
    };
    const backToAttendedMenuItem = (params) => {
        if (params.row.status === 'TREATMENT_STARTED') {
            return <MenuItem
                onClick={() => handleBacktoAttendedPopup(params.row.lead_id, params.row)}
            >
                <Iconify icon={'jam:refresh-reverse'} />
                Back to Attended
            </MenuItem>;
        }
        return null;
    };
    const archiveLeadMenuItem = (params) => <MenuItem
        sx={{ color: 'error.main' }}
        onClick={() => handleArchivedDetails(params.row.lead_id, params.row)}
    >
        <Iconify icon={'eva:archive-fill'} />
        Archive Lead
    </MenuItem>;
    const restoreLeadMenuItem = (params) => <MenuItem
        onClick={() => handleRestore(params.row)}
    >
        <Iconify icon={'ic:sharp-restore'} />
        Restore
    </MenuItem>;
    const markUnMarkDuplicateMenuItem = (params) => {
        if (params.row?.is_duplicate) {
            return <MenuItem
                onClick={() => handleUnMarkAsDuplicate(params.row)}
            >
                <Iconify icon={'pepicons-pop:duplicate-off'} />
                Unmark as duplicate
            </MenuItem>;
        }
        return <MenuItem
            onClick={() => handleMarkAsDuplicate(params.row)}
        >
            <Iconify icon={'ion:duplicate'} />
            Mark as duplicate
        </MenuItem>;
    };

    const completeToStartMenuItem = (params) => {
        if (tableTitle === 'Completed Leads' && params.row.status === 'CLOSED') {
            return (
                <Tooltip title='Move back to start treatment' placement='bottom' arrow>
                    <MenuItem
                        onClick={() => handleCompletedToStartTreatment(params.row)}>
                        <Iconify icon={'bxs:flag'} /> Start Treatment
                    </MenuItem>
                </Tooltip>
            );
        }
        return null;

    };

    const handleViewDetailClick = (params) => {
        dispatch(leadPage(tableTitle ? { name: tableTitle, path: tablePath } : {}));
        navigate(`/dashboard/practice/${practiceId}/lead-detail/${params.row.lead_id}`);
    };

    const handleTagCellClick = (params) => {
        if (typed === 'ArchiveTable') {
            handleArchivedNavigate(params.row);
            return;
        };
        setSelectedData(params?.row);
    };
    const commonTagsProops = {
        handleTagCellClick,
        typed,
        selectData,
        handleSelect,
        reloadCounter,
        updateLeadPage,
        setTableData,
        handleArchivedNavigate
    };

    const contactCellProps = {
        anchorRef,
        open,
        setOpen,
        isMobile,
        buttonHandleClose,
        handlePopup,
        viewDetailBtn,
        handleCompleteTreatment,
        handleAttendedClick,
        handleAttendedFailedClick,
        handleAttendedPopup,
        handleSyncDentally,
        syncingLeadIds,
    };

    const createdAtDateCellProps = {
        handleChangeDateFormat,
        showDate,
        handleNavigate,
        formatData,
        typed,
        methods,
        setValue,
        dateFormat,
    };

    const columns = [
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...EmailCell() },
        { ...phoneNumberCell },
        { ...treatmentCell },
        ...(typed === 'DashNewLeadTable' ? [] : [{ ...StatusCell({ onChange: handleNavigate }) }]),
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        {
            field: 'contact',
            headerName: 'Action',
            sortable: false,
            minWidth: 210,
            align: 'center',
            renderCell: (params) => {
                if ((params?.row?.treatment_name === '' || null) && typed !== 'ArchiveTable' && isReportData) {
                    return (
                        <CustomSelectTreatmentCell params={params} handleNext={handleNext} />
                    );
                }
                return <ContactCell row={params?.row} {...contactCellProps} />;
            },
        },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        ...(showPracticeColumn ? [] : [
            { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
            { ...TagCell({ ...commonTagsProops }) },
            { ...ViewDetailCell({ leadPage, tableTitle, tablePath, interPractice: showPracticeColumn }) },
            {
                ...actionColumnData,
                getActions: (params) => [
                    <div className='action-custom-cell' key={params.row.id}>
                        <DataGridTableMoreMenu
                            open={openMenu === params.row.id}
                            sx={{ width: '180px' }}
                            anchorEl={menuCurrentTarget}
                            type={params.row.status === 'CONSULTATION_BOOKED' ? 'FinancialTableRow' : ''}
                            onOpen={(event) => handleOpenMenu(event, params)}
                            onClose={() => setOpenMenuActions(null)}
                            actions={
                                <>
                                    {typed !== 'ArchiveTable' ?
                                        <Box sx={tableMoreMenuBoxStyle}>
                                            <CustomViewDetailComponent handleClick={() => handleViewDetailClick(params)} />
                                            {params.row.status !== 'CLOSED' && params.row.status !== 'TREATMENT_STARTED' && params?.row?.appointments?.[0]?.status !== 'CANCELLED' && (typed !== 'ArchiveTable' && isReportData && params.row.status !== '' && <MenuItem onClick={() => params.row.status !== 'CONSULTATION_BOOKED' && setOpenMenuActions(null)}>
                                                {params.row.is_archive_pending ?
                                                    <CustomArchivingButton params={params} />
                                                    :
                                                    params.row.status === 'CONSULTATION_BOOKED' && !params.row.is_archive_pending ?
                                                        <Box>
                                                            <CustomRecordAttendanceBtn params={params} handleMoreMenuTogglePopper={handleMoreMenuTogglePopper} moreMenuOpen={moreMenuOpen} ref={anchorMoreMenuRef} />
                                                            <Popper
                                                                sx={{
                                                                    zIndex: 1399,
                                                                    marginLeft: '10px !important'
                                                                }}
                                                                open={isMoreMenuPopperOpen(params.row.lead_id)}
                                                                anchorEl={anchorMoreMenuRef.current}
                                                                role={undefined}
                                                                transition
                                                                placement={isMobile ? 'left-start' : 'right-start'}
                                                            >
                                                                {({ TransitionProps }) => (
                                                                    <Grow
                                                                        {...TransitionProps}
                                                                    >
                                                                        <Paper>
                                                                            <ClickAwayListener onClickAway={buttonHandleClose}>
                                                                                <MenuList id='split-button-menu' autoFocusItem sx={menuListStyle}>
                                                                                    <MenuItem
                                                                                        key='Attended'
                                                                                        onClick={() => { setOpen(false); setMoreMenuOpen(false); setOpenMenuActions(null); handleAttendedClick(params.row.lead_id, params.row); }}
                                                                                        sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                                    >
                                                                                        <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'akar-icons:check'} /> Attended
                                                                                    </MenuItem>
                                                                                    <MenuItem
                                                                                        key='Failed to attend'
                                                                                        onClick={() => { setOpen(false); setMoreMenuOpen(false); setOpenMenuActions(null); handleAttendedFailedClick(params.row.lead_id, params.row); }}
                                                                                        sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                                    >
                                                                                        <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'ci:close-big'} /> Failed to attend
                                                                                    </MenuItem>
                                                                                </MenuList>
                                                                            </ClickAwayListener>
                                                                        </Paper>
                                                                    </Grow>
                                                                )}
                                                            </Popper>
                                                        </Box>
                                                        : params.row.status === 'CONSULTATION_ATTENDED' && !params.row.is_archive_pending ?
                                                            <CustomFollowUpBtn params={params} handleAttendedPopup={handleAttendedPopup} />
                                                            : params.row.status === 'CLOSED' && !params.row.is_archive_pending || params.row.status === 'TREATMENT_STARTED' && !params.row.is_archive_pending ?
                                                                <CustomCompleteTreatmentBtn params={params} viewDetailBtn={viewDetailBtn} handleCompleteTreatment={handleCompleteTreatment} />
                                                                : params.row.treatment_name === '' || null ? '' :
                                                                    <CustomFollowUpBtnV2 params={params} handlePopup={handlePopup} />
                                                }
                                            </MenuItem>)}
                                            <StartTreatmentMenuItem handleStartTreatment={handleStartTreatment} params={params} />
                                            <CompleteTreatmentMenuItem handleCompleteTreatment={handleCompleteTreatment} params={params} />
                                            {params?.row?.appointments?.[0]?.status === 'CANCELLED' ? null : editAppointmentMenuItem(params)}
                                            {markUnMarkDuplicateMenuItem(params)}
                                            <MarkAsSpamMenuItem handleMarkAsSpam={handleMarkAsSpam} params={params} />
                                            {backToAttendedMenuItem(params)}
                                            {archiveLeadMenuItem(params)}
                                            {googleReviewMenuItem(params)}
                                        </Box>
                                        :
                                        restoreLeadMenuItem(params)
                                    }
                                </>
                            }
                        />
                    </div>
                ]
            }
        ])
    ];

    const getMultiTreatmentColumns = (column) => {
        if (tableTitle === 'Consultation Attended Leads' || tableTitle === 'Attended Checklists' || tableTitle === 'Treatment Started Leads') {
            return commonColumns?.map((ele) => column.find((element) => element.field === ele));
        }
        return commonColumns?.filter((ele) => ele !== 'price').map((ele) => column.find((element) => element.field === ele));
    };

    const multiTreatmentColumn = [
        {
            ...treatmentFieldColumnData,
            cellClassName: (params) => getTreatmentCellClassName(params?.row?.treatmentBgColor),
            renderCell: (params) =>
                <Tooltip
                    title={`${params?.row?.treatment_name || ''} ${params?.row?.leadTreatmentSpecification?.teeth_count
                        ? `(x${params?.row?.leadTreatmentSpecification?.teeth_count})` : ''}`}
                    placement='bottom'
                    TransitionComponent={Zoom}
                    TransitionProps={{ timeout: 300 }}
                >
                    <Box onClick={(e) => handleTreatmentClick(e, params?.row)} sx={tableCellInnerBoxStyle}>
                        <Typography
                            variant='body2'
                            sx={{ ...treatmentNameCellTypoStyle, color: getTextColor(params?.row?.treatmentBgColor) }}
                        >
                            <span style={treatmentNameStyle(params?.row?.treatmentData?.length > 1)}>
                                {params?.row?.treatment_name || ''}
                                {'  '}
                                {params?.row?.leadTreatmentSpecification?.teeth_count ? `(x${params?.row?.leadTreatmentSpecification?.teeth_count})` : ''}
                            </span>
                        </Typography>
                    </Box></Tooltip>,
        },
        { ...TreatmentPriceCell({ renderPriceEditInputCell, renderPriceCell }) },
        { ...StatusCell({ onChange: typed !== 'ArchiveTable' ? handleNavigate : handleArchivedNavigate }) },
        { ...DepositPendingCell({ updateLeadPage, reloadCounter, typed, handleSetTableData }) },
        { ...ApptMethodCell({ typed, handleSetTableData }) },
        { ...ApptTypeCell({ handleSetTableData, reloadCounter, typed, updateLeadPage }) },
        { ...DateOfAppointmentCell({ ...attendedDateCellProps }) },
        { ...BookedWithCell({ openAppointmentAssignTaskFuc, typed, updateLeadPage, reloadCounter }) },
        {
            ...actionColumnData,
            getActions: (params) => [
                <div className='action-custom-cell' key={params.row.id}>
                    <DataGridTableMoreMenu
                        open={openMenu === params.row.id}
                        sx={{ width: '180px' }}
                        anchorEl={menuCurrentTarget}
                        type={params.row.status === 'CONSULTATION_BOOKED' ? 'FinancialTableRow' : ''}
                        onOpen={(event) => handleOpenMenu(event, params)}
                        onClose={() => setOpenMenuActions(null)}
                        actions={typed !== 'ArchiveTable' ?
                            <Box sx={tableMoreMenuBoxStyle}>
                                <CustomViewDetailComponent handleClick={() => handleViewDetailClick(params)} />
                                {completeToStartMenuItem(params)}
                                {params.row.status !== 'CLOSED' && params.row.status !== 'TREATMENT_STARTED' && (typed !== 'ArchiveTable' && isReportData && <MenuItem onClick={() => params.row.status !== 'CONSULTATION_BOOKED' && setOpenMenuActions(null)}>
                                    {
                                        params.row.status === 'CONSULTATION_BOOKED' ?
                                            <Box>
                                                <CustomRecordAttendanceBtn params={params} handleMoreMenuTogglePopper={handleMoreMenuTogglePopper} moreMenuOpen={moreMenuOpen} ref={anchorMoreMenuRef} />
                                                <Popper
                                                    sx={{
                                                        zIndex: 1399,
                                                        marginLeft: '10px !important'
                                                    }}
                                                    open={isMoreMenuPopperOpen(params.row.lead_id)}
                                                    anchorEl={anchorMoreMenuRef.current}
                                                    role={undefined}
                                                    transition
                                                    placement={isMobile ? 'left-start' : 'right-start'}
                                                >
                                                    {({ TransitionProps }) => (
                                                        <Grow
                                                            {...TransitionProps}
                                                        >
                                                            <Paper>
                                                                <ClickAwayListener onClickAway={buttonHandleClose}>
                                                                    <MenuList id='split-button-menu' autoFocusItem sx={menuListStyle}>
                                                                        <MenuItem
                                                                            key='Attended'
                                                                            onClick={() => { setOpen(false); setMoreMenuOpen(false); setOpenMenuActions(null); handleAttendedClick(params.row.lead_id, params.row); }}
                                                                            sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                        >
                                                                            <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'akar-icons:check'} /> Attended
                                                                        </MenuItem>
                                                                        <MenuItem
                                                                            key='Failed to attend'
                                                                            onClick={() => { setOpen(false); setMoreMenuOpen(false); setOpenMenuActions(null); handleAttendedFailedClick(params.row.lead_id, params.row); }}
                                                                            sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                        >
                                                                            <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'ci:close-big'} /> Failed to attend
                                                                        </MenuItem>
                                                                    </MenuList>
                                                                </ClickAwayListener>
                                                            </Paper>
                                                        </Grow>
                                                    )}
                                                </Popper>
                                            </Box>
                                            : params.row.status === 'CONSULTATION_ATTENDED' ?
                                                <CustomFollowUpBtn params={params} handleAttendedPopup={handleAttendedPopup} />
                                                : params.row.status === 'CLOSED' && params.row.status === 'TREATMENT_STARTED' ?
                                                    <CustomCompleteTreatmentBtn params={params} viewDetailBtn={viewDetailBtn} handleCompleteTreatment={handleCompleteTreatment} />
                                                    : params.row.treatment_name === '' || null ? '' :
                                                        <CustomFollowUpBtnV2 params={params} handlePopup={handlePopup} />
                                    }
                                </MenuItem>)}
                                {(params.row.status === 'TREATMENT_STARTED') &&
                                    <MenuItem
                                        onClick={() => handleCompleteTreatment(params.row)}
                                    >
                                        <Iconify icon={'fluent:cloud-sync-complete-24-filled'} />
                                        Complete Treatment
                                    </MenuItem>
                                }
                            </Box>
                            :
                            <>
                                <MenuItem
                                    onClick={() => handleRestore(params.row)}
                                >
                                    <Iconify icon={'ic:sharp-restore'} />
                                    Restore
                                </MenuItem>
                                <MenuItem
                                    onClick={() => archiveViewDetails(params)}
                                >
                                    <Iconify icon={'mdi:user-box'} />
                                    View Details
                                </MenuItem>
                            </>
                        }
                    />
                </div>
            ]
        }
    ];

    const newLeadsColumns = [
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...EmailCell() },
        { ...phoneNumberCell },
        { ...treatmentCell },
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        {
            field: 'contact',
            headerName: 'Action',
            sortable: false,
            minWidth: 180,
            align: 'center',
            renderCell: (params) => {
                if ((params?.row?.treatment_name === '' || null) && typed !== 'ArchiveTable' && isReportData) {
                    return (
                        <CustomSelectTreatmentCell params={params} handleNext={handleNext} />
                    );
                }
                return <ContactCell row={params?.row} {...contactCellProps} />;
            },
        },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
        { ...TagCell({ ...commonTagsProops }) },
        { ...ViewDetailCell({ leadPage, tableTitle, tablePath }) },
        {
            ...actionColumnData,
            getActions: (params) => [
                <div className='action-custom-cell' key={params.row.id}>
                    <DataGridTableMoreMenu
                        open={openMenu === params.row.id}
                        sx={{ width: '180px' }}
                        anchorEl={menuCurrentTarget}
                        type={params.row.status === 'CONSULTATION_BOOKED' ? 'FinancialTableRow' : ''}
                        onOpen={(event) => handleOpenMenu(event, params)}
                        onClose={() => setOpenMenuActions(null)}
                        actions={
                            <>
                                {typed !== 'ArchiveTable' ?
                                    <Box sx={tableMoreMenuBoxStyle}>
                                        <CustomViewDetailComponent handleClick={() => handleViewDetailClick(params)} />
                                        {params.row.status !== 'CLOSED' && params.row.status !== '' && (typed !== 'ArchiveTable' && isReportData && <MenuItem onClick={() => setOpenMenuActions(null)}>
                                            {params.row.is_archive_pending ?
                                                <CustomArchivingButton params={params} />
                                                :
                                                params.row.status === 'CONSULTATION_BOOKED' && !params.row.is_archive_pending ?
                                                    <Box>
                                                        <CustomRecordAttendanceBtn params={params} handleMoreMenuTogglePopper={handleMoreMenuTogglePopper} moreMenuOpen={open} ref={anchorMoreMenuRef} />
                                                        <Popper
                                                            sx={{
                                                                zIndex: 1,
                                                                marginLeft: '10px !important'
                                                            }}
                                                            open={isMoreMenuPopperOpen(params.row.lead_id)}
                                                            anchorEl={anchorMoreMenuRef.current}
                                                            role={undefined}
                                                            transition
                                                            placement={isMobile ? 'left-start' : 'right-start'}
                                                        >
                                                            {({ TransitionProps }) => (
                                                                <Grow
                                                                    {...TransitionProps}
                                                                >
                                                                    <Paper>
                                                                        <ClickAwayListener onClickAway={buttonHandleClose}>
                                                                            <MenuList id='split-button-menu' autoFocusItem sx={menuListStyle}>
                                                                                <MenuItem
                                                                                    key='Attended'
                                                                                    onClick={() => handleAttendedClick(params.row.lead_id, params.row)}
                                                                                    sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                                >
                                                                                    <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'akar-icons:check'} /> Attended
                                                                                </MenuItem>
                                                                                <MenuItem
                                                                                    key='Failed to attend'
                                                                                    onClick={() => handleAttendedFailedClick(params.row.lead_id, params.row)}
                                                                                    sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                                >
                                                                                    <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'ci:close-big'} /> Failed to attend
                                                                                </MenuItem>
                                                                            </MenuList>
                                                                        </ClickAwayListener>
                                                                    </Paper>
                                                                </Grow>
                                                            )}
                                                        </Popper>
                                                    </Box>
                                                    : params.row.status === 'CONSULTATION_ATTENDED' && !params.row.is_archive_pending ?
                                                        <CustomFollowUpBtn params={params} handleAttendedPopup={handleAttendedPopup} />
                                                        : params.row.status === 'CLOSED' && !params.row.is_archive_pending || params.row.status === 'TREATMENT_STARTED' && !params.row.is_archive_pending ?
                                                            <CustomCompleteTreatmentBtn params={params} viewDetailBtn={viewDetailBtn} handleCompleteTreatment={handleCompleteTreatment} />
                                                            : params.row.treatment_name === '' || null ? '' :
                                                                <CustomFollowUpBtnV2 params={params} handlePopup={handlePopup} />
                                            }
                                        </MenuItem>)}
                                        <StartTreatmentMenuItem handleStartTreatment={handleStartTreatment} params={params} />
                                        <CompleteTreatmentMenuItem handleCompleteTreatment={handleCompleteTreatment} params={params} />
                                        {editAppointmentMenuItem(params)}
                                        {markUnMarkDuplicateMenuItem(params)}
                                        <MarkAsSpamMenuItem handleMarkAsSpam={handleMarkAsSpam} params={params} />
                                        {completeToStartMenuItem(params)}
                                        {backToAttendedMenuItem(params)}
                                        {archiveLeadMenuItem(params)}
                                        {googleReviewMenuItem(params)}
                                    </Box>
                                    :
                                    restoreLeadMenuItem(params)
                                }
                            </>
                        }
                    />
                </div>
            ]
        }
    ];

    const consultationBookedLeadsColumns = [
        { ...DateOfAppointmentCell({ ...attendedDateCellProps }) },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        { ...EmailCell() },
        { ...DepositPendingCell({ updateLeadPage, reloadCounter, typed, handleSetTableData }) },
        { ...CustomDepositPaymentCell(selectedRow, handleDeleteTreatmentPayment) },
        { ...ApptMethodCell({ typed, handleSetTableData }) },
        { ...ApptTypeCell({ handleSetTableData, reloadCounter, typed, updateLeadPage }) },
        { ...BookedWithCell({ openAppointmentAssignTaskFuc, typed, updateLeadPage, reloadCounter }) },
        { ...phoneNumberCell },
        { ...treatmentCell },
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        { ...ContactActionCell({ ...contactCellProps, minWidth: 210 }) },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        { ...TagCell({ ...commonTagsProops }) },
        { ...ViewDetailCell({ leadPage, tableTitle, tablePath, classNames: 'view_details' }) },
        {
            ...actionColumnData,
            getActions: (params) => [
                <div className='action-custom-cell' key={params.row.id}>
                    <DataGridTableMoreMenu
                        open={openMenu === params.row.id}
                        sx={{ width: '180px' }}
                        anchorEl={menuCurrentTarget}
                        type={params.row.status === 'CONSULTATION_BOOKED' ? 'FinancialTableRow' : ''}
                        onOpen={(event) => handleOpenMenu(event, params)}
                        onClose={() => setOpenMenuActions(null)}
                        actions={
                            <>
                                {typed !== 'ArchiveTable' ?
                                    <Box sx={tableMoreMenuBoxStyle}>
                                        <CustomViewDetailComponent handleClick={() => handleViewDetailClick(params)} />
                                        {params.row.status !== 'CLOSED' && (typed !== 'ArchiveTable' && isReportData && params?.row?.appointments?.[0]?.status !== 'CANCELLED' && <MenuItem onClick={() => params.row.status !== 'CONSULTATION_BOOKED' && setOpenMenuActions(null)}>
                                            {params.row.is_archive_pending ?
                                                <CustomArchivingButton params={params} />
                                                :
                                                params.row.status === 'CONSULTATION_BOOKED' && !params.row.is_archive_pending ?
                                                    <Box>
                                                        <CustomRecordAttendanceBtn params={params} handleMoreMenuTogglePopper={handleMoreMenuTogglePopper} moreMenuOpen={moreMenuOpen} ref={anchorMoreMenuRef} />
                                                        <Popper
                                                            sx={{
                                                                zIndex: 1399,
                                                                marginLeft: '10px !important'
                                                            }}
                                                            open={isMoreMenuPopperOpen(params.row.lead_id)}
                                                            anchorEl={anchorMoreMenuRef.current}
                                                            role={undefined}
                                                            transition
                                                            placement={isMobile ? 'left-start' : 'right-start'}
                                                        >
                                                            {({ TransitionProps }) => (
                                                                <Grow
                                                                    {...TransitionProps}
                                                                >
                                                                    <Paper>
                                                                        <ClickAwayListener onClickAway={buttonHandleClose}>
                                                                            <MenuList id='split-button-menu' autoFocusItem sx={menuListStyle}>
                                                                                <MenuItem
                                                                                    key='Attended'
                                                                                    onClick={() => { setOpen(false); setMoreMenuOpen(false); setOpenMenuActions(null); handleAttendedClick(params.row.lead_id, params.row); }}
                                                                                    sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                                >
                                                                                    <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'akar-icons:check'} /> Attended
                                                                                </MenuItem>
                                                                                <MenuItem
                                                                                    key='Failed to attend'
                                                                                    onClick={() => { setOpen(false); setMoreMenuOpen(false); setOpenMenuActions(null); handleAttendedFailedClick(params.row.lead_id, params.row); }}
                                                                                    sx={{ borderRadius: '8px', minHeight: '40px !important', color: slateGray }}
                                                                                >
                                                                                    <Iconify style={{ color: slateGray, marginRight: 15 }} width={20} height={20} icon={'ci:close-big'} /> Failed to attend
                                                                                </MenuItem>
                                                                            </MenuList>
                                                                        </ClickAwayListener>
                                                                    </Paper>
                                                                </Grow>
                                                            )}
                                                        </Popper>
                                                    </Box>
                                                    : params.row.status === 'CONSULTATION_ATTENDED' && !params.row.is_archive_pending ?
                                                        <CustomFollowUpBtn params={params} handleAttendedPopup={handleAttendedPopup} />
                                                        : params.row.status === 'CLOSED' && !params.row.is_archive_pending || params.row.status === 'TREATMENT_STARTED' && !params.row.is_archive_pending ?
                                                            <CustomCompleteTreatmentBtn params={params} viewDetailBtn={viewDetailBtn} handleCompleteTreatment={handleCompleteTreatment} />
                                                            : params.row.treatment_name === '' || null ? '' :
                                                                <CustomFollowUpBtnV2 params={params} handlePopup={handlePopup} />
                                            }
                                        </MenuItem>)}
                                        <StartTreatmentMenuItem handleStartTreatment={handleStartTreatment} params={params} />
                                        <CompleteTreatmentMenuItem handleCompleteTreatment={handleCompleteTreatment} params={params} />
                                        {params?.row?.appointments?.[0]?.status === 'CANCELLED' ? null : editAppointmentMenuItem(params)}
                                        {markUnMarkDuplicateMenuItem(params)}
                                        <MarkAsSpamMenuItem handleMarkAsSpam={handleMarkAsSpam} params={params} />
                                        {backToAttendedMenuItem(params)}
                                        {archiveLeadMenuItem(params)}
                                        {googleReviewMenuItem(params)}
                                    </Box>
                                    :
                                    restoreLeadMenuItem(params)
                                }
                            </>
                        }
                    />
                </div>
            ]
        }
    ];

    const commonMenuProps = {
        openMenu,
        menuCurrentTarget,
        handleOpenMenu,
        setOpenMenuActions,
        typed,
        handleViewDetailClick,
        isReportData,
        anchorMoreMenuRef,
        setOpen,
        open,
        handleAttendedClick,
        handleAttendedFailedClick,
        buttonHandleClose,
        handleAttendedPopup,
        viewDetailBtn,
        handlePopup,
        handleStartTreatment,
        handleCompleteTreatment,
        editAppointmentMenuItem,
        backToAttendedMenuItem,
        restoreLeadMenuItem,
        googleReviewMenuItem,
        theme,
    };

    const attendedColumnActionProps = {
        ...commonMenuProps,
        markUnMarkDuplicateMenuItem,
        archiveLeadMenuItem,
    };

    const consultationAttendedLeadsColumns = [
        {
            ...AttendedDateCell({
                methods,
                handleChangeDateFormat,
                setValue,
                typed,
                updateLeadPage,
                reloadCounter,
                openAppointmentAssignTaskFuc
            })
        },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        { ...EmailCell() },
        { ...DepositPendingCell({ updateLeadPage, reloadCounter, typed, handleSetTableData }) },
        { ...CustomDepositPaymentCell(selectedRow, handleDeleteTreatmentPayment) },
        { ...ClincheckStatusCell({ typed, handleClinCheckStatusShow, theme }) },
        { ...ApptMethodCell({ typed, handleSetTableData }) },
        { ...ApptTypeCell({ handleSetTableData, reloadCounter, typed, updateLeadPage }) },
        {
            field: 'booked_with',
            headerName: 'Booked with',
            width: 150,
            minWidth: 140,
            editable: false,
            sortable: false,
            renderCell: (params) => <BookedWithField
                rowData={params.row}
                OpenAssignTask={openAppointmentAssignTaskFuc}
                tableType={typed}
                updateLeadPage={updateLeadPage}
                reloadCounter={reloadCounter}
            />
        },
        { ...phoneNumberCell },
        { ...treatmentCell },
        { ...TreatmentPriceCell({ renderPriceEditInputCell, renderPriceCell }) },
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        { ...ContactActionCell({ ...contactCellProps, minWidth: 210 }) },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        {
            field: 'lead_treatments__lead_contacted_counts__count',
            headerName: 'Times contacted',
            width: 130,
            minWidth: 100,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (params) => params.row?.lead_treatments__lead_contacted_counts__count ?? 0,
        },
        { ...TagCell({ ...commonTagsProops }) },
        { ...ViewDetailCell({ leadPage, tableTitle, tablePath }) },
        { ...AttendedTableColumnActions(attendedColumnActionProps) }
    ];

    const attendedChecklistsColumns = [
        { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...EmailCell() },
        {
            ...AttendedDateCell({
                methods,
                handleChangeDateFormat,
                setValue,
                typed,
                updateLeadPage,
                reloadCounter,
                openAppointmentAssignTaskFuc
            })
        },
        {
            field: 'booked_with',
            headerName: 'Booked with',
            width: 150,
            minWidth: 140,
            editable: false,
            sortable: false,
            renderCell: (params) => <BookedWithField
                rowData={params.row}
                OpenAssignTask={openAppointmentAssignTaskFuc}
                tableType={typed}
                updateLeadPage={updateLeadPage}
                reloadCounter={reloadCounter}
            />
        },
        { ...treatmentCell },
        { ...TreatmentPriceCell({ renderPriceEditInputCell, renderPriceCell }) },
        { ...TagCell({ ...commonTagsProops }) },
        { ...AttendedTableColumnActions(attendedColumnActionProps) },
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        { ...DepositPendingCell({ updateLeadPage, reloadCounter, typed, handleSetTableData }) },
        { ...CustomDepositPaymentCell(selectedRow, handleDeleteTreatmentPayment) },
        { ...ClincheckStatusCell({ typed, handleClinCheckStatusShow, theme }) },
        { ...ApptMethodCell({ typed, handleSetTableData }) },
        { ...ApptTypeCell({ handleSetTableData, reloadCounter, typed, updateLeadPage }) },
        { ...phoneNumberCell },
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        { ...ContactActionCell({ ...contactCellProps, minWidth: 210 }) },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        { ...ViewDetailCell({ leadPage, tableTitle, tablePath }) },
    ];

    const consultationClincheckLeadsColumns = [
        {
            ...AttendedDateCell({
                methods,
                handleChangeDateFormat,
                setValue,
                typed,
                updateLeadPage,
                reloadCounter,
                openAppointmentAssignTaskFuc
            })
        },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        { ...EmailCell() },
        { ...DepositPendingCell({ updateLeadPage, reloadCounter, typed, handleSetTableData }) },
        { ...CustomDepositPaymentCell(selectedRow, handleDeleteTreatmentPayment) },
        { ...ClincheckStatusCell({ typed, handleClinCheckStatusShow, theme }) },
        { ...ApptMethodCell({ typed, handleSetTableData }) },
        { ...ApptTypeCell({ handleSetTableData, reloadCounter, typed, updateLeadPage }) },
        {
            field: 'booked_with',
            headerName: 'Booked with',
            width: 150,
            minWidth: 140,
            editable: false,
            sortable: false,
            renderCell: (params) => <BookedWithField
                rowData={params.row}
                OpenAssignTask={openAppointmentAssignTaskFuc}
                tableType={typed}
                updateLeadPage={updateLeadPage}
                reloadCounter={reloadCounter}
            />
        },
        { ...phoneNumberCell },
        {
            ...treatmentFieldColumnData,
            cellClassName: (params) => getTreatmentCellClassName(params?.row?.treatmentBgColor),
            renderCell: (params) => {
                if ((params?.row?.treatment_name === '' || null) && typed !== 'ArchiveTable' && isReportData) {
                    return (
                        <IconButton
                            color='primary'
                            aria-label='Add treatment'
                            component='label'
                            onClick={() => handleNext(params?.row)}
                            disabled={params?.row?.is_archive_pending}
                        >
                            <Iconify
                                icon='material-symbols:add-circle'
                                style={{ color: 'primary' }}
                                sx={{ mr: '0' }}
                                width={24}
                                height={24}
                            />
                        </IconButton>
                    );
                }
                return (
                    <CustomTreatmentNameCell
                        params={params}
                        handleTreatmentClick={handleTreatmentClick}
                        ref={apiRef}
                    />
                );
            },
        },
        { ...TreatmentPriceCell({ renderPriceEditInputCell, renderPriceCell }) },
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        { ...ContactActionCell({ ...contactCellProps, minWidth: 210 }) },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        { ...TagCell({ ...commonTagsProops }) },
        { ...ViewDetailCell({ leadPage, tableTitle, tablePath }) },
        { ...AttendedTableColumnActions(attendedColumnActionProps) }
    ];

    const treatmentStartedLeadsColumns = [
        {
            ...TransitionDateCell({
                typed,
                showTransitionDate,
                updateLeadPage,
                reloadCounter,
                getTransitionDate
            })
        },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        { ...AssigneeCell({ getAssignees, OpenAssignTask }) },
        { ...CreatedAtDateCell(createdAtDateCellProps) },
        { ...EmailCell() },
        { ...CustomDepositPaymentCell(selectedRow, handleDeleteTreatmentPayment) },
        {
            ...ConsultationDateCell({
                typed,
                openAppointmentAssignTaskFuc,
                methods,
                setValue,
                handleChangeDateFormat,
                updateLeadPage,
                reloadCounter,
            })
        },
        { ...ConsultationBookedWithCell({ openAppointmentAssignTaskFuc }) },
        { ...DateOfAppointmentCell({ ...attendedDateCellProps }) },
        { ...BookedWithCell({ openAppointmentAssignTaskFuc, typed, updateLeadPage, reloadCounter }) },
        { ...phoneNumberCell },
        { ...treatmentCell },
        { ...TreatmentPriceCell({ renderPriceEditInputCell, renderPriceCell }) },
        { ...ReferralCell({ updateLeadPage, reloadCounter, typed, setTableData }) },
        toggleCell,
        { ...ContactActionCell(contactCellProps) },
        { ...LastContactCell({ methods, handleChangeDateFormat, setValue, handleNavigate }) },
        { ...TagCell({ ...commonTagsProops }) },
        { ...ViewDetailCell({ leadPage, tableTitle, tablePath }) },
        {
            ...StartedTableColumnActions({
                ...commonMenuProps,
                handleAddAppointment
            })
        }
    ];

    // FTA table: FTA date → Lead Name → rest of newLeadsColumns (without name cols)
    const ftaLeadsColumns = [
        { ...attendedDateCell },
        ...(isCombinedName ? [{ ...LeadNameCell() }] : [{ ...FirstNameCell() }, { ...LastNameCell() }]),
        ...newLeadsColumns.filter((c) => !['lead_name', 'first_name', 'last_name'].includes(c?.field)),
    ];

    const archiveTableProps = {
        methods,
        handleChangeDateFormat,
        setValue,
        handleArchivedNavigate,
        typed,
        handleTreatmentClick,
        handleRestore,
        commonTagsProops,
        archiveViewDetails,
        openMenu,
        menuCurrentTarget,
        handleOpenMenu,
        setOpenMenuActions,
        isReportData,
        anchorMoreMenuRef,
        setOpen,
        open,
        handleAttendedClick,
        buttonHandleClose,
        handleAttendedFailedClick,
        handleAttendedPopup,
        viewDetailBtn,
        handlePopup,
        handleViewDetailClick,
        handleStartTreatment,
        editAppointmentMenuItem,
        markUnMarkDuplicateMenuItem,
        backToAttendedMenuItem,
        archiveLeadMenuItem,
        apiRef,
        handleCompleteTreatment,
        isCombinedName,
        theme,
        dateArchiveFormat,
        reasons,
        toggleCell,
        getAssignees,
        OpenAssignTask,
    };

    const getLoadingState = (type) => {
        // Bug 1 fix: when the checklist-strip tab switch is in flight, keep the grid
        // loading until BOTH the new rows AND their checklist cells have resolved.
        // This prevents the stale-data frame where the old rows show with blank
        // checklist cells (different lead IDs not yet in the new checklist data).
        // Scoped to this path only via the checklistSwitching prop — does not
        // change handleLoading's 500ms timer for any other table or path.
        // Implicit blast-radius guard: checklistSwitching is ONLY ever set to true
        // by handleChangeChecklistState in NewLead.jsx, which is only reachable when
        // the user is on a custom tab with checklist_binding active (the checklist
        // sub-strip is not rendered for any other table). All 12+ other table types
        // that don't pass checklistSwitching receive it as undefined/false → this
        // flag is always false for them → zero impact on their loading behaviour.
        const checklistBusy = checklistSwitching && taskChecklistLoading;
        if (type === 'ConsultantClinCheckAttend') return (pageCount === undefined || archivedLoading || loadingForClincheckLead || tablePreferenceLoading || forceLoading || checklistBusy);
        return (pageCount === undefined || archivedLoading || loadingForLead || tablePreferenceLoading || forceLoading || checklistBusy);
    };

    const handleDetailPanelExpandedRowIdsChange = useCallback((newIds) => {
        setDetailPanelExpandedRowIds(
            newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds,
        );
    }, []);

    const getDetailPanelHeight = useCallback(() => 'auto', []);

    const handlePageChange = (pageData) => {
        setPaginationModel(pageData);
        handlePaginationChange(pageData);
    };

    const handleSortModelChange = (sortModel, details) => {
        if (sortModel?.[0]?.field || sortModel?.[0]?.sort) {
            handleSortingChange({ userId, key: sortModel?.[0]?.field, order: sortModel?.[0]?.sort, details, tableName: tableTitle });
        } else {
            setSortModel(sortModel);
            sortingApiCall('created_at', 'desc');
            UpdateTableData(typed, { order: 'desc', orderValue: 'created_at', perPage: 10, pageNumber: 0 });
            setPaginationModel({ pageSize: 10, page: 0 });
        }
    };

    const getDetailPanel = () => {
        if (isMultiTreatmentTable || typed === 'AttendedChecklists') return {};
        return {
            getDetailPanelContent: ({ row }) => <GetDetailPanelContent
                row={row}
                tableTitle={tableTitle}
                typed={typed}
                isReportData={isReportData}
                selectedRow={selectedRow}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                handlePaginationChange={handlePaginationChange}
                pageCount={pageCount}
                handleChangePerSize={handleChangePerSize}
                OpenAssignTask={OpenAssignTask}
                reloadCounter={reloadCounter}
                updateLeadPage={updateLeadPage}
                openBulkArchive={open}
                onSelectionModelChange={onSelectionModelChange}
                forceUpdate={forceUpdate}
                tablePath={tablePath}
                sortingApiCall={sortingApiCall}
                columnVisibilityModel={columnVisibilityModel}
                handleColumnVisibilityChange={handleColumnVisibilityChange}
                handleColumnOrderChange={handleColumnOrderChange}
                isMultiTreatmentTable
                handleReloadAPI={handleReloadAPI}
                setCurrentTreatmentData={setCurrentTreatmentData}
                openAppointmentAssignTaskFuc={openAppointmentAssignTaskFuc}
                setTableData={setTableData}
                handleSetTableData={handleSetTableData}
            />,
            getDetailPanelHeight,
            detailPanelExpandedRowIds,
            onDetailPanelExpandedRowIdsChange: handleDetailPanelExpandedRowIdsChange
        };
    };

    const userId = useGetCurrentUserId();

    const handlePinnedColumnsChange = useCallback((updatedPinnedColumns) => {
        const right = updatedPinnedColumns?.right || [];
        const hasAnyActionCol = ACTION_GROUP.some((col) => right.includes(col));
        if (hasAnyActionCol) {
            const otherRight = right.filter((col) => !ACTION_GROUP.includes(col));
            updatedPinnedColumns = { ...updatedPinnedColumns, right: [...otherRight, ...ACTION_GROUP] };
        }
        // Defer the Redux dispatch: MUI DataGrid's internal XState machine throws
        // "not allowed to update several sub-state in one transaction" when pinned
        // columns update collides with pagination/visibility changes in the same
        // render cycle (Sentry: REACT-DD, 2 users). queueMicrotask defers to after
        // the current synchronous cycle without a visible delay.
        queueMicrotask(() => {
            ChangePinnedColumns({ updatedPinnedColumns, setPinnedColumns: pinProps.setPinnedColumns, userId, tableName: tableTitle });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const addDynamicColumns = (columns, choicesData) => {
        if (isMultiTreatmentTable) return columns;
        choicesData?.forEach((choice) => {
            if (choice.metadata && choice.metadata.tables && choice.metadata.tables.includes(tableTitle)) {
                columns.push({
                    field: choice.name,
                    headerName: capitalizeFirstLetter(choice.name),
                    sortable: false,
                    editable: false,
                    width: 120,
                    minWidth: 90,
                    renderCell: (params) => (
                        <ChoicesField
                            rowData={params.row}
                            updateLeadPage={updateLeadPage}
                            reloadCounter={reloadCounter}
                            tableType={typed}
                            handleSetTableData={handleSetTableData}
                            choiceValue={choice}
                            choicesData={choicesData}
                        />
                    ),
                });
            }
        });

        return columns;
    };

    const dynamicColumns = getColumns(
        addDynamicColumns(
            applyTablePreferences({
                columns: getTableColumns({
                    tableTitle,
                    newLeadsColumns,
                    consultationBookedLeadsColumns,
                    consultationAttendedLeadsColumns,
                    consultationClincheckLeadsColumns,
                    treatmentStartedLeadsColumns,
                    attendedChecklistsColumns,
                    ftaLeadsColumns,
                    archivedLeadsColumns: ArchivedLeadsColumns(archiveTableProps),
                    columns,
                }),
                tableName: tableTitle,
                isMultiTreatmentTable
            }), choicesData) || [], tableTitle);

    let dynamicColumnsOverridden = dynamicColumns;
    if (tableTitle === 'All Patients') {
        dynamicColumnsOverridden = (dynamicColumns || []).map((c) => (c?.field === 'status' ? { ...c, headerName: 'Current Status' } : c));
        const hasTotal = dynamicColumnsOverridden.some((c) => c?.field === 'treatment_count');
        if (!hasTotal) {
            const totalCol = {
                field: 'treatment_count',
                headerName: 'Total Treatment Count',
                width: 180,
                sortable: false,
                valueGetter: (params) => {
                    const row = params?.row || {};
                    if (Array.isArray(row?.treatmentData)) return row.treatmentData.length;
                    if (Array.isArray(row?.lead_treatments)) return row.lead_treatments.length;
                    return 0;
                },
            };
            const insertAt = Math.min(5, dynamicColumnsOverridden.length);
            dynamicColumnsOverridden.splice(insertAt, 0, totalCol);
        }
    }

    // ── Tasks V2 checklist columns ───────────────────────────────────────────
    const MAX_TASK_CHECKLIST_COLUMNS = 8;

    // Key on a stable ID-signature string so visibleLeadIds only changes when
    // the actual set of visible lead IDs changes, not on every tableData reference swap.
    const idSignature = useMemo(
        () => (tableData || []).map((r) => Number(r.lead_id || (typeof r.id === 'string' ? parseInt(r.id, 10) : r.id))).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => Number(a) - Number(b)).join(','),
        [tableData]
    );
    const visibleLeadIds = useMemo(
        () => idSignature ? idSignature.split(',').map(Number) : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [idSignature]
    );

    const {
        columns: taskChecklistCols,
        data: taskChecklistData,
        loading: taskChecklistLoading,
        error: taskChecklistError,
        refresh: refreshTaskChecklist,
    // tableTitle flows into the hook as the BE ?table= param (canonicalised inside the hook via
    // SUB_TAB_TO_PARENT_TABLE). It is also used separately as the column-visibility persistence key
    // by useColumnVisibilityChange — keep the raw sub-tab string here; do NOT canonicalise at this call site.
    // activeSubTab is the currently active sub-tab value (e.g. "2" for Follow up 2) — passed as &sub_tab=
    // so the BE can filter checklist columns by applies_to_sub_tab targeting.
    // suppressTaskChecklistColumns: gates the hook entirely. Used by the "All" parent view
    // of ConsultantAttend when the practice has checklist-bound custom tabs siblings —
    // without suppression, the BE returns columns via `show_in_tables` and leaks the
    // per-custom-tab checklist UI onto the unfiltered list (Raj-reported 2026-05-25,
    // practice 92, follow-up to task #100 Risk 2).
    } = useTaskChecklistColumns(practiceId, visibleLeadIds, tableTitle, activeSubTab, activeCustomTabSlug, suppressTaskChecklistColumns);

    // Bug 1 fix: notify parent (NewLead) once BOTH the main lead rows AND the
    // checklist cell data have resolved so it can drop the isChecklistSwitching gate.
    // Fires whenever loadingForLead or taskChecklistLoading transitions to false.
    // Safety: taskChecklistLoading always eventually reaches false (success, error,
    // abort, or disabled path) so this cannot produce a permanent skeleton.
    //
    // I-1 timing coupling: relies on coreThunks.js LEAD_LOADING_SETTLE_MS (500 ms)
    // being LONGER than useTaskChecklistColumns' 150 ms debounce. This ordering
    // means loadingForLead stays true while the checklist fetch completes, so both
    // flags reach false together. Cross-ref: coreThunks.js LEAD_LOADING_SETTLE_MS.
    //
    // M-3 (defer / known gap): on rapid double-switch the 1st switch's gate
    // may clear while the 2nd is still in flight (the 2nd setIsChecklistSwitching
    // fires before the 1st onChecklistSwitchingDone resolves). Bounded — the 2nd
    // switch's own loading cycle then correctly re-enables and clears the gate,
    // self-healing within one fetch cycle. No code change warranted now.
    useEffect(() => {
        if (!checklistSwitching) return;
        if (loadingForLead) return;
        if (taskChecklistLoading) return;
        // Both rows and checklist data are settled — clear the busy gate.
        onChecklistSwitchingDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checklistSwitching, loadingForLead, taskChecklistLoading]);

    // handleTablePreference runs before async checklist cols arrive; merge them in
    // when they land so they aren't absent from the columnVisibilityModel.
    useEffect(() => {
        if (!taskChecklistCols?.length || !setColumnVisibilityModel) return;
        setColumnVisibilityModel((prev) => {
            const updates = {};
            taskChecklistCols.forEach((col) => {
                const field = `task_checklist_${col.id}`;
                if (prev[field] !== false) updates[field] = true;
            });
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskChecklistCols]);

    // Popover anchor for the overflow "+N more" cell — keyed by leadId so each row opens its own popover
    const [overflowPopoverState, setOverflowPopoverState] = useState({ anchor: null, leadId: null });
    const handleOverflowOpen = (e, leadId) => { e.stopPropagation(); setOverflowPopoverState({ anchor: e.currentTarget, leadId }); };
    const handleOverflowClose = () => setOverflowPopoverState({ anchor: null, leadId: null });

    // Stable column definition objects: created once per unique col.id, then
    // renderCell is mutated in-place so finalColumns reference stays stable across
    // data-only refreshes (pagination, filter changes that return the same columns).
    const stableColDefsRef = useRef({});
    const stableOverflowColRef = useRef(null);

    // Hold latest cell data in a ref so renderCell closures always read fresh data.
    // Updated synchronously each render (not via effect) so renderCell sees current data
    // on every DataGrid paint without causing the column-def useMemo to recompute.
    const taskChecklistDataRef = useRef(taskChecklistData);
    taskChecklistDataRef.current = taskChecklistData;

    // Track items marked YES/SKIPPED this session so allTerminal can fire without
    // waiting for a BE refetch. Keys are leadId strings, values are Sets of itemId strings.
    // Symmetric sessionReopenedRef tracks items flipped back to PENDING so wasAllTerminal
    // can detect the reverse transition even when the ref still shows stale terminal data.
    const sessionCompletedRef = useRef({});
    const sessionReopenedRef = useRef({});

    // Track the last built column array by its sorted-ID signature for stable-reference returns.
    const prevDynamicColsRef = useRef({ sig: '', cols: [] });

    // ── Checklist-complete transition snackbar ──────────────────────────────
    // { taskId, itemId, templateId, leadId, previousState } | null
    const [checklistCompleteUndo, setChecklistCompleteUndo] = useState(null);

    const handleChecklistUndoClose = useCallback(() => setChecklistCompleteUndo(null), []);

    const handleChecklistUndo = useCallback(async () => {
        if (!checklistCompleteUndo) return;
        const { taskId, itemId, leadId } = checklistCompleteUndo;
        setChecklistCompleteUndo(null);
        // M1: cancel the failsafe BEFORE the network round-trip.  If the await takes > 1 s
        // the old post-await clearTimeout could arrive after the timer already fired.
        clearTimeout(failsafeTimerRef.current);
        try {
            await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/reopen/`,
                'POST',
                {}
            );
            refreshTaskChecklist();
            // Restore the row optimistically with a slide-in animation.
            // Look up by leadId so the correct slot is restored even if multiple leads were
            // removed concurrently (Fix #3: Map instead of single shared ref).
            const saved = removedRowRef.current.get(String(leadId));
            if (restoreRowOptimistically && saved) {
                restoreRowOptimistically(saved.row, saved.index);
                removedRowRef.current.delete(String(leadId));
                setOptimisticRemovedCount((c) => Math.max(0, c - 1));
                setEnteringRowIds((prev) => new Set([...prev, String(leadId)]));
                setTimeout(() => {
                    setEnteringRowIds((prev) => {
                        const next = new Set(prev);
                        next.delete(String(leadId));
                        return next;
                    });
                }, 400);
            } else if (refreshTable) {
                refreshTable();
            }
            if (refreshCounters) refreshCounters();
        } catch {
            dispatch(updateError({ success: false, message: 'Could not undo — please refresh.' }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checklistCompleteUndo, practiceId, refreshTable, restoreRowOptimistically, refreshCounters]);

    // Ctrl+Z / Cmd+Z when snackbar is open
    useEffect(() => {
        if (!checklistCompleteUndo) return undefined;
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                handleChecklistUndo();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [checklistCompleteUndo, handleChecklistUndo]);

    // Tracks the 6.5 s reconcile timers spawned by animateAndRemoveBulkLead — one per
    // bulk-completed lead (unlike the single-lead failsafeTimerRef which only holds one).
    // Stored in a Set so unmount cleanup can cancel all of them.
    const bulkReconcileTimersRef = useRef(new Set());

    // L1: on unmount cancel any pending failsafe timer (makeOnChecklistComplete at 6.5 s
    // or makeOnChecklistReopen at 5 s) and all bulk-reconcile timers to prevent
    // setState-after-unmount warnings.
    useEffect(() => () => {
        clearTimeout(failsafeTimerRef.current);
        bulkReconcileTimersRef.current.forEach(clearTimeout);
    }, []);

    // Factory: returns a callback for a specific cell (templateId + leadId).
    // Called by every cell type after a YES post (via useStatusApi or direct callApi).
    // Always refreshes badge counts (cheap). Only re-fetches the full list when all
    // items for this template+lead are terminal — the lead is only removed from the
    // In Progress list at that point, so earlier re-fetches would just flash the table
    // and return the same lead in the same row.
    const makeOnChecklistComplete = useCallback((templateId, leadId, taskId, itemId) => (previousState) => {
        // Register this item as session-completed so allTerminal doesn't wait for a BE refetch.
        const lid = String(leadId);
        if (!sessionCompletedRef.current[lid]) sessionCompletedRef.current[lid] = new Set();
        sessionCompletedRef.current[lid].add(String(itemId));
        // If this item was previously reopened in this session, un-mark it.
        sessionReopenedRef.current[lid]?.delete(String(itemId));

        // taskChecklistData is keyed by numeric lead id; lid may be the composite
        // DataGrid row id ("leadId-idx") on V2-flat. Strip the suffix for the data lookup.
        // Row-identity ops below (exit animation, removeRowOptimistically) keep the
        // composite leadId since the DataGrid row id is composite on V2-flat.
        const dataLid = lid.split('-')[0];
        const allCells = taskChecklistDataRef.current?.[dataLid] || {};
        const templateCols = Object.values(allCells);
        const templateColKeys = Object.keys(allCells);
        const TERMINAL = new Set(['YES', 'SKIPPED']);
        const sessionDone = sessionCompletedRef.current[lid] || new Set();
        const allTerminal = templateCols.length > 0 && templateCols.every(
            (c, i) => TERMINAL.has(c.completion_state) || sessionDone.has(String(c.checklist_item_id || c.id || templateColKeys[i]))
        );
        if (allTerminal) {
            // Counts only change when a lead becomes all-terminal and leaves this
            // sub-tab — so refresh badges/widgets here, not on every intermediate
            // yes/no (which would fire N count queries per click for no count change).
            if (refreshCounters) refreshCounters();
            // Animate the row out, then splice it from the local data — no full refetch.
            setExitingRowIds((prev) => new Set([...prev, String(leadId)]));
            setTimeout(() => {
                setExitingRowIds((prev) => {
                    const next = new Set(prev);
                    next.delete(String(leadId));
                    return next;
                });
                if (removeRowOptimistically) {
                    // Fix #3: key by leadId so concurrent removals don't clobber each other.
                    const removed = removeRowOptimistically(leadId);
                    if (removed) {
                        removedRowRef.current.set(String(leadId), removed);
                        // Fix #1: keep rowCount in sync so DataGrid doesn't render phantom rows.
                        setOptimisticRemovedCount((c) => c + 1);
                    }
                }
                // Clear session tracking for this lead — row is gone, state is clean.
                delete sessionCompletedRef.current[String(leadId)];
                delete sessionReopenedRef.current[String(leadId)];
                // Failsafe: silent background reconcile after 6.5 s to catch any drift.
                // M2: pushed from 5 s → 6.5 s so it can never fire inside the 6 s undo window
                // (Snackbar autoHideDuration=6000).  Timer ID stored so handleChecklistUndo
                // can cancel it when undo fires (Fix #2 / M1).
                failsafeTimerRef.current = setTimeout(() => {
                    if (refreshTable) refreshTable();
                    // After the reconcile the server state is authoritative — reset count.
                    setOptimisticRemovedCount((c) => Math.max(0, c - 1));
                    removedRowRef.current.delete(String(leadId));
                }, 6500);
            }, 380);
            setChecklistCompleteUndo({ taskId, itemId, templateId, leadId, previousState });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshCounters, refreshTable, removeRowOptimistically]);

    // Bulk-complete: per-lead exit animation + optimistic removal — same sequence as
    // makeOnChecklistComplete (exit CSS → 380 ms → removeRowOptimistically → 6.5 s reconcile),
    // but driven externally (from FloatingBulkActionBar via NewLead.jsx → onBulkCompleteReady).
    const animateAndRemoveBulkLead = useCallback((leadId) => {
        const lid = String(leadId);
        setExitingRowIds((prev) => new Set([...prev, lid]));
        setTimeout(() => {
            setExitingRowIds((prev) => {
                const next = new Set(prev);
                next.delete(lid);
                return next;
            });
            if (removeRowOptimistically) {
                const removed = removeRowOptimistically(leadId);
                if (removed) {
                    setOptimisticRemovedCount((c) => c + 1);
                    // Bulk-complete has no undo snackbar — removedRowRef.current.set() is
                    // intentionally omitted here (no restore path needed). .delete() below
                    // only clears any stale entry left by a prior single-lead completion on
                    // the same lead within the same session.
                    const timerId = setTimeout(() => {
                        setOptimisticRemovedCount((c) => Math.max(0, c - 1));
                        removedRowRef.current.delete(lid);
                        bulkReconcileTimersRef.current.delete(timerId);
                    }, 6500);
                    bulkReconcileTimersRef.current.add(timerId);
                }
            }
        }, 380);
    }, [removeRowOptimistically]);

    // Register the animator with NewLead.jsx so the bulk-complete handler can call it
    // without needing to reach inside the grid directly.
    useEffect(() => {
        if (onBulkCompleteReady) onBulkCompleteReady(animateAndRemoveBulkLead);
    // Re-register whenever the callback identity changes (removeRowOptimistically reference).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onBulkCompleteReady, animateAndRemoveBulkLead]);

    // Factory: returns an async gate called by ChecklistCell BEFORE firing the reopen PATCH.
    // Resolves true immediately if the lead is not all-terminal (mid-checklist edit, no dialog needed).
    // Resolves true/false based on user confirmation if the lead is all-terminal (complete→in_progress).
    const makeOnBeforeReopen = useCallback((leadId, leadData) => () => {
        const lid = String(leadId);
        const allCells = taskChecklistDataRef.current?.[lid.split('-')[0]] || {};
        const templateCols = Object.values(allCells);
        const TERMINAL = new Set(['YES', 'SKIPPED']);
        const sessionDone = sessionCompletedRef.current[lid] || new Set();
        const sessionPending = sessionReopenedRef.current[lid] || new Set();
        const wasAllTerminal = templateCols.length > 0 && templateCols.every(
            (c) => {
                const cid = String(c.checklist_item_id || c.id);
                if (sessionPending.has(cid)) return false;
                return TERMINAL.has(c.completion_state) || sessionDone.has(cid);
            }
        );
        if (!wasAllTerminal) return Promise.resolve(true);
        const firstName = leadData?.first_name || '';
        const lastName = leadData?.last_name || '';
        const leadName = `${firstName} ${lastName}`.trim() || 'This lead';
        return new Promise((resolve) => {
            reopenConfirmResolverRef.current = resolve;
            setReopenConfirm({ leadName });
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Factory: returns a callback fired when a terminal cell is reopened (YES/SKIPPED → PENDING).
    // Detects if the lead WAS in "complete" state (all other cells terminal) — if so, it just
    // transitioned back to in_progress and should leave the current (Follow-Up Complete) view.
    // eslint-disable-next-line no-unused-vars
    const makeOnChecklistReopen = useCallback((templateId, leadId, _taskId, itemId) => (_previousState) => {
        if (refreshCounters) refreshCounters();

        // Register this item as session-reopened so wasAllTerminal can detect
        // the complete→in_progress transition even against stale ref data.
        const lid = String(leadId);
        if (!sessionReopenedRef.current[lid]) sessionReopenedRef.current[lid] = new Set();
        sessionReopenedRef.current[lid].add(String(itemId));
        sessionCompletedRef.current[lid]?.delete(String(itemId));

        const allCells = taskChecklistDataRef.current?.[lid.split('-')[0]] || {};
        const templateCols = Object.values(allCells);
        const TERMINAL = new Set(['YES', 'SKIPPED']);
        const sessionDone = sessionCompletedRef.current[lid] || new Set();
        const sessionPending = sessionReopenedRef.current[lid] || new Set();
        // Was the lead all-terminal BEFORE this reopen? Factor in session tracking so
        // the check works even when the ref still shows stale data from before refetch.
        const wasAllTerminal = templateCols.length > 0 && templateCols.every(
            (c) => {
                const cid = String(c.checklist_item_id || c.id);
                // The item being reopened right now: was it terminal before? Yes — that's why
                // it's being reopened. Exclude it from the "still pending" filter since
                // sessionReopenedRef already includes it and we want to know prior state.
                if (cid === String(itemId)) return TERMINAL.has(c.completion_state) || sessionDone.has(cid);
                if (sessionPending.has(cid)) return false;
                return TERMINAL.has(c.completion_state) || sessionDone.has(cid);
            }
        );
        if (wasAllTerminal) {
            // The lead is transitioning complete→in_progress — animate it out of this view.
            setExitingRowIds((prev) => new Set([...prev, String(leadId)]));
            setTimeout(() => {
                setExitingRowIds((prev) => {
                    const next = new Set(prev);
                    next.delete(String(leadId));
                    return next;
                });
                if (removeRowOptimistically) {
                    // Fix #3: key by leadId (Map) so concurrent removals don't clobber each other.
                    const removed = removeRowOptimistically(leadId);
                    if (removed) {
                        removedRowRef.current.set(String(leadId), removed);
                        // Fix #1: keep rowCount in sync so DataGrid doesn't render phantom rows.
                        setOptimisticRemovedCount((c) => c + 1);
                    }
                }
                delete sessionCompletedRef.current[lid];
                delete sessionReopenedRef.current[lid];
                // L1: store timer ID so the unmount cleanup can cancel it and prevent
                // setState-after-unmount.  Reuses failsafeTimerRef — this flow (reopen)
                // and the complete flow run on different tabs and are mutually exclusive.
                failsafeTimerRef.current = setTimeout(() => {
                    if (refreshTable) refreshTable();
                    // After reconcile, server state is authoritative — reset the count/entry.
                    setOptimisticRemovedCount((c) => Math.max(0, c - 1));
                    removedRowRef.current.delete(String(leadId));
                }, 5000);
            }, 380);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshCounters, refreshTable, removeRowOptimistically]);

    const dynamicTaskColumns = useMemo(() => {
        if (!taskChecklistCols || taskChecklistCols.length === 0) {
            stableColDefsRef.current = {};
            stableOverflowColRef.current = null;
            return [];
        }

        // Sort by (template_id, sort_order, id) for deterministic column order across fetches.
        const sortedCols = [...taskChecklistCols].sort((a, b) => {
            if (a.template_id !== b.template_id) return String(a.template_id).localeCompare(String(b.template_id));
            if (a.sort_order !== b.sort_order) return (a.sort_order ?? 0) - (b.sort_order ?? 0);
            return (a.id ?? 0) - (b.id ?? 0);
        });

        const visibleCols = sortedCols.slice(0, MAX_TASK_CHECKLIST_COLUMNS);

        const hasOverflow = taskChecklistCols.length > MAX_TASK_CHECKLIST_COLUMNS;
        const hiddenCols = hasOverflow ? taskChecklistCols.slice(MAX_TASK_CHECKLIST_COLUMNS) : [];

        // If only taskChecklistData changed (same column IDs, order, and overflow count), skip
        // rebuilding the result array — mutate renderCell in-place and return the same array
        // reference so finalColumns (and DataGrid) don't see a new columns prop.
        const newSig = `${visibleCols.map((c) => c.id).join(',')}|${hiddenCols.length}`;
        const colStructureUnchanged = newSig === prevDynamicColsRef.current.sig && prevDynamicColsRef.current.cols.length > 0;
        const overflowCount = hiddenCols.length;

        const result = [];

        visibleCols.forEach((col) => {
            const key = col.id;
            if (!stableColDefsRef.current[key]) {
                // First time: create the definition object
                stableColDefsRef.current[key] = {
                    field: `task_checklist_${col.id}`,
                    headerName: col.title,
                    description: col.title,
                    width: 140,
                    sortable: false,
                    disableColumnMenu: true,
                    align: 'center',
                    headerAlign: 'center',
                    renderHeader: () => (
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.3, textAlign: 'center', padding: '4px 0' }}>
                            {col.title}
                            {col.is_required && <span style={{ color: '#FF4842', marginLeft: 2 }}>*</span>}
                        </div>
                    ),
                    renderCell: null, // will be set below
                    _taskChecklistColumn: true,
                    _templateName: col.template_name,
                    _templateId: col.template_id,
                    _sortOrder: col.sort_order,
                };
            }

            // Always update renderCell in-place — this avoids creating new column objects
            // while still keeping cell content fresh when data/loading state changes.
            // Only show skeletons on the initial load (no data yet). Background refreshes
            // (e.g. after marking a cell done) keep the prior data rendered so other rows
            // don't flash to a loading state.
            const isInitialLoad = taskChecklistLoading && !Object.keys(taskChecklistData || {}).length;
            if (isInitialLoad) {
                stableColDefsRef.current[key].renderCell = () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1, width: '100%' }}>
                        <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5 }} />
                    </Box>
                );
            } else if (taskChecklistError) {
                stableColDefsRef.current[key].renderCell = () => (
                    <Tooltip title="Failed to load checklist columns">
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, color: 'error.main' }}>
                            <Iconify icon="eva:alert-circle-outline" width={18} />
                        </Box>
                    </Tooltip>
                );
            } else {
                stableColDefsRef.current[key].renderCell = (params) => {
                    // V2-flat rows use a composite DataGrid id (`${leadId}-${treatmentIndex}`),
                    // but taskChecklistData is keyed by the numeric lead id. Strip the suffix
                    // so the lookup hits. No-op on V1 tables (numeric id has no `-idx`).
                    const leadId = String(params.row?.id ?? '').split('-')[0] || String(params.row?.lead_id ?? '');
                    const rawCellData = taskChecklistDataRef.current?.[leadId]?.[col.id] || null;
                    const cellData = rawCellData ? {
                        checklist_item_id: col.id,
                        item_type: col.item_type,
                        ...rawCellData,
                        template_item_id: col.id,
                    } : null;
                    return (
                        <TaskChecklistField
                            cellData={cellData}
                            colMeta={col}
                            practiceId={practiceId}
                            leadData={params.row}
                            onUpdate={refreshTaskChecklist}
                            onSelectedRow={selectedRow}
                            onChecklistComplete={makeOnChecklistComplete(col.template_id, String(params.row?.id || params.row?.lead_id || ''), cellData?.task_id, cellData?.checklist_item_id)}
                            onChecklistReopen={makeOnChecklistReopen(col.template_id, String(params.row?.id || params.row?.lead_id || ''), cellData?.task_id, cellData?.checklist_item_id)}
                            onBeforeReopen={makeOnBeforeReopen(String(params.row?.id || params.row?.lead_id || ''), params.row)}
                        />
                    );
                };
            }

            result.push(stableColDefsRef.current[key]);
        });

        // Remove stale keys for columns that are no longer visible
        const visibleKeySet = new Set(visibleCols.map((c) => c.id));
        Object.keys(stableColDefsRef.current).forEach((k) => {
            if (!visibleKeySet.has(Number(k))) delete stableColDefsRef.current[k];
        });

        if (hasOverflow) {
            if (!stableOverflowColRef.current) {
                stableOverflowColRef.current = {
                    field: 'task_checklist_overflow',
                    headerName: `+${overflowCount} more`,
                    width: 90,
                    sortable: false,
                    disableColumnMenu: true,
                    renderCell: null,
                };
            }
            stableOverflowColRef.current.headerName = `+${overflowCount} more`;
            stableOverflowColRef.current.renderCell = (params) => {
                const leadId = String(params.row?.id || params.row?.lead_id || '');
                return (
                    <Tooltip title="View hidden checklist items">
                        <Box
                            onClick={(e) => handleOverflowOpen(e, leadId)}
                            sx={{ display: 'flex', alignItems: 'center', px: 1, cursor: 'pointer', color: 'primary.main', fontSize: '0.75rem', fontWeight: 600 }}
                        >
                            <Iconify icon="eva:more-horizontal-fill" width={18} sx={{ mr: 0.5 }} />
                            {`+${overflowCount}`}
                        </Box>
                    </Tooltip>
                );
            };
            result.push(stableOverflowColRef.current);
        } else {
            stableOverflowColRef.current = null;
        }

        // Return the same array reference when only data changed (renderCell already
        // mutated in-place above). This keeps finalColumns stable and prevents DataGrid
        // from triggering a full column re-order on every mark-complete / data refresh.
        if (colStructureUnchanged) return prevDynamicColsRef.current.cols;
        prevDynamicColsRef.current = { sig: newSig, cols: result };
        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskChecklistCols, taskChecklistData, taskChecklistLoading, taskChecklistError, practiceId, refreshTaskChecklist]);

    const finalColumns = useMemo(() => {
        if (!dynamicTaskColumns.length) return dynamicColumnsOverridden;
        const result = [...dynamicColumnsOverridden];
        const actionFields = new Set(ACTION_GROUP);

        if (checklistColumnInsertion === 'after_treatment') {
            const treatmentIdx = result.findIndex((c) => c.field === 'treatment');
            if (treatmentIdx !== -1) {
                result.splice(treatmentIdx + 1, 0, ...dynamicTaskColumns);
                return result;
            }
        } else if (checklistColumnInsertion === 'before_action') {
            const insertBefore = result.findIndex((c) => actionFields.has(c.field));
            if (insertBefore !== -1) {
                result.splice(insertBefore, 0, ...dynamicTaskColumns);
                return result;
            }
            return [...result, ...dynamicTaskColumns];
        } else if (checklistColumnInsertion === 'at_end') {
            return [...result, ...dynamicTaskColumns];
        }

        // Default: after_lead_name (also fallback for after_treatment when treatment col absent)
        const leadNameIdx = result.findIndex((c) => c.field === 'lead_name');
        if (leadNameIdx !== -1) {
            result.splice(leadNameIdx + 1, 0, ...dynamicTaskColumns);
            return result;
        }
        const insertBefore = result.findIndex((c) => actionFields.has(c.field));
        if (insertBefore === -1) return [...result, ...dynamicTaskColumns];
        result.splice(insertBefore, 0, ...dynamicTaskColumns);
        return result;
    }, [dynamicColumnsOverridden, dynamicTaskColumns, checklistColumnInsertion]);
    // ────────────────────────────────────────────────────────────────────────

    const getProps = () => {
        if (isMultiTreatmentTable) return {};
        return { onColumnWidthChange: (data) => handleColumnWidthChange({ data, tableName: tableTitle, userId, details: { api: apiRef?.current } }) };
    };

    const getGridColumns = multiTreatmentCheck => {
        const columnList = multiTreatmentCheck ? getMultiTreatmentColumns(multiTreatmentColumn) : finalColumns;
        if (!showPracticeColumn)
            return columnList
        return [{
            field: "Practice Name",
            headerName: capitalizeFirstLetter("Practice Name"),
            sortable: false,
            editable: false,
            minWidth: 200,
            align: 'left',
            renderCell: (params) =>
                <Tooltip
                    title={params?.row?.practiceName}
                    placement='bottom'
                    TransitionComponent={Zoom}
                    TransitionProps={{ timeout: 300 }}
                >
                    <Box onClick={(e) => handlePracticeChangeClick(e, params?.row)} sx={tableCellInnerBoxStyle}>
                        <Typography
                            variant='body2'
                            sx={{ ...treatmentNameCellTypoStyle, color: getTextColor(params?.row?.treatmentBgColor) }}
                        >
                            <span style={treatmentNameStyle(params?.row?.treatmentData?.length > 1)}>
                                {params?.row?.practiceName}
                            </span>
                        </Typography>
                    </Box></Tooltip>,
        }, ...columnList]
    }

    const { slots: dataGridSlots, slotProps: dataGridSlotProps } = DataGridSlots({ isMultiTreatmentTable, tableTitle, practiceId, openBulkArchive, openBulkReminder, hasActiveFilters, onClearFilters, onResetColumns: isEnforcedByPractice ? null : pinProps?.resetColumns, onToggleCombineName: isEnforcedByPractice ? null : handleToggleCombineName, isCombinedName, isEnforcedByPractice });

    // Derive hidden cols for the overflow popover (same slice as useMemo above)
    const overflowHiddenCols = taskChecklistCols ? taskChecklistCols.slice(MAX_TASK_CHECKLIST_COLUMNS) : [];
    const overflowLeadId = overflowPopoverState.leadId;
    const overflowRowData = overflowLeadId ? (tableData || []).find((r) => String(r.id || r.lead_id) === overflowLeadId) : null;

    return (
        <>
        <Popover
            open={Boolean(overflowPopoverState.anchor)}
            anchorEl={overflowPopoverState.anchor}
            onClose={handleOverflowClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    minWidth: 220,
                    maxWidth: 320,
                    p: 0,
                    overflow: 'hidden',
                },
            }}
        >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                    More checklist items
                </Typography>
            </Box>
            {overflowHiddenCols.map((col, idx) => {
                // taskChecklistData is keyed by numeric lead id; overflowLeadId is the composite
                // DataGrid row id on V2-flat. Strip the suffix for the data lookup.
                const rawCellData = taskChecklistData?.[String(overflowLeadId).split('-')[0]]?.[col.id] || null;
                const cellData = rawCellData ? {
                    checklist_item_id: col.id,
                    item_type: col.item_type,
                    ...rawCellData,
                    template_item_id: col.id,
                } : null;
                return (
                    <Box key={col.id}>
                        {idx > 0 && <Divider />}
                        <Box sx={{ px: 1.5, py: 1.25 }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.78rem', color: 'text.primary' }}>
                                {col.title}
                                {col.is_required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
                            </Typography>
                            <TaskChecklistField
                                cellData={cellData}
                                colMeta={col}
                                practiceId={practiceId}
                                leadData={overflowRowData}
                                onUpdate={() => { refreshTaskChecklist(); handleOverflowClose(); }}
                                onSelectedRow={selectedRow}
                                onChecklistComplete={makeOnChecklistComplete(col.template_id, String(overflowRowData?.id || overflowRowData?.lead_id || ''), cellData?.task_id, cellData?.checklist_item_id)}
                                onChecklistReopen={makeOnChecklistReopen(col.template_id, String(overflowRowData?.id || overflowRowData?.lead_id || ''), cellData?.task_id, cellData?.checklist_item_id)}
                                onBeforeReopen={makeOnBeforeReopen(String(overflowRowData?.id || overflowRowData?.lead_id || ''), overflowRowData)}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Popover>
            {/* Mobile view toggle */}
            {isMobile && (
                <Box sx={{ display: 'flex', gap: '4px', mb: 1, px: 1 }}>
                    <Box
                        component="button"
                        onClick={() => handleMobileViewToggle('cards')}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            px: '12px', py: '6px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                            border: '1px solid', cursor: 'pointer',
                            borderColor: mobileViewMode === 'cards' ? '#00A76F' : 'rgba(145,158,171,0.24)',
                            bgcolor: mobileViewMode === 'cards' ? 'rgba(0,167,111,0.08)' : 'transparent',
                            color: mobileViewMode === 'cards' ? '#007B55' : '#637381',
                        }}
                    >
                        Cards
                    </Box>
                    <Box
                        component="button"
                        onClick={() => handleMobileViewToggle('table')}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            px: '12px', py: '6px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                            border: '1px solid', cursor: 'pointer',
                            borderColor: mobileViewMode === 'table' ? '#00A76F' : 'rgba(145,158,171,0.24)',
                            bgcolor: mobileViewMode === 'table' ? 'rgba(0,167,111,0.08)' : 'transparent',
                            color: mobileViewMode === 'table' ? '#007B55' : '#637381',
                        }}
                    >
                        Table
                    </Box>
                </Box>
            )}

            {/* Mobile card view */}
            {isMobile && mobileViewMode === 'cards' && (
                <Box sx={{ bgcolor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                    {getLoadingState(typed) ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">Loading...</Typography>
                        </Box>
                    ) : tableData?.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">No leads found</Typography>
                        </Box>
                    ) : (
                        tableData.map((row) => (
                            <MobileLeadCard
                                key={row.id}
                                row={row}
                                onContact={(r) => selectedRow(r, 'followUpLead')}
                                onMoreAction={(r, action) => {
                                    if (action === 'archive') selectedRow(r, 'archivedLead');
                                }}
                                typed={typed}
                            />
                        ))
                    )}
                </Box>
            )}

            {/* DataGrid — always mounted (hidden on mobile cards mode to preserve hooks) */}
            <Box sx={{ ...(isMobile && mobileViewMode === 'cards' && { display: 'none' }) }}>
                <DataGridPro
                    key={`${forceUpdate}`}
                    checkboxSelection={!isMultiTreatmentTable && !showPracticeColumn}
                    disableRowSelectionOnClick
                    keepNonExistentRowsSelected
                    columnHeaderHeight={72}
                    rowHeight={150}
                    getRowHeight={() => 'auto'}
                    autoHeight
                    rows={tableData}
                    columns={getGridColumns(isMultiTreatmentTable)}
                    apiRef={apiRef}
                    editMode='cell'
                    processRowUpdate={(newRow, oldRow) => processRowUpdate(newRow, oldRow, handleSetTableData)}
                    sortingMode='server'
                    disableColumnMenu={isMultiTreatmentTable}
                    pinnedColumns={pinProps?.pinnedColumns}
                    onPinnedColumnsChange={handlePinnedColumnsChange}
                    {...getProps()}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    getRowClassName={(params) => {
                        const base = getRowClassName(params, tableTitle);
                        const rowId = String(params.row?.id || params.row?.lead_id || '');
                        if (exitingRowIds.has(rowId)) return `${base} row-exiting`.trim();
                        if (enteringRowIds.has(rowId)) return `${base} row-entering`.trim();
                        return base;
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    pagination
                    paginationMode='server'
                    paginationModel={paginationModel}
                    className={`leads-datagrid-table ${isMultiTreatmentTable ? 'detail-panel-table' : ''}`}
                    initialState={initialPaginationData}
                    rowCount={
                        // Fix #1 / L2: when a row has been optimistically spliced out, the server
                        // total (pageCount) is still N while the grid only has N-1 rows.
                        // MUI DataGrid Pro renders blank filler rows to fill the gap.
                        // Subtracting optimisticRemovedCount keeps the two in sync without
                        // waiting for a full refetch.  For non-checklist tables this is always
                        // 0, so behaviour is identical to the original `pageCount`.
                        // L2: pass undefined through so MUI can distinguish "unknown total"
                        // from "zero rows" — avoids the grid locking pagination controls.
                        pageCount === undefined ? undefined : Math.max(0, pageCount - optimisticRemovedCount)
                    }
                    onRowSelectionModelChange={(newSelectionModel) => onSelectionModelChange(newSelectionModel)}
                    disableColumnFilter
                    disableColumnReorder={isMultiTreatmentTable || isEnforcedByPractice}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onColumnOrderChange={() => handleColumnOrderChange(handleGetVisibleColumns(apiRef))}
                    onPaginationModelChange={handlePageChange}
                    onPageSizeChange={(newPageSize) => handleChangePerSize(newPageSize)}
                    loading={getLoadingState(typed)}
                    sx={leadDetailTabsGridStyle(getLoadingState())}
                    {...getDetailPanel()}
                    slots={dataGridSlots}
                    slotProps={dataGridSlotProps}
                    hideFooterPagination={isMultiTreatmentTable}
                />
            </Box>
            <Snackbar
                open={Boolean(checklistCompleteUndo)}
                autoHideDuration={6000}
                onClose={handleChecklistUndoClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ bottom: 96 }} // clear floating bulk action bar (~88px tall + 8px gap)
                message="Lead moved to Follow-Up Complete"
                ContentProps={{ 'data-testid': 'checklist-complete-snackbar' }}
                action={(
                    <>
                        <Button color="primary" size="small" onClick={handleChecklistUndo} data-testid="checklist-undo-btn">
                            Undo
                        </Button>
                        <IconButton size="small" color="inherit" onClick={handleChecklistUndoClose} aria-label="Close">
                            <Iconify icon="eva:close-outline" width={16} />
                        </IconButton>
                    </>
                )}
            />
            {reopenConfirm && (
                <Dialog
                    open={Boolean(reopenConfirm)}
                    onClose={() => {
                        setReopenConfirm(null);
                        if (reopenConfirmResolverRef.current) {
                            reopenConfirmResolverRef.current(false);
                            reopenConfirmResolverRef.current = null;
                        }
                    }}
                    maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: '14px', maxWidth: '380px' } }}
                >
                    <DialogTitle sx={{ pb: 0.5, fontWeight: 600, fontSize: '1rem' }}>
                        Re-open this checklist?
                    </DialogTitle>
                    <DialogContent sx={{ pt: '8px !important' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            <strong>{reopenConfirm.leadName}</strong> will move back to <strong>In Progress</strong>. Their Follow-Up Complete status will be cleared.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                            onClick={() => {
                                setReopenConfirm(null);
                                if (reopenConfirmResolverRef.current) {
                                    reopenConfirmResolverRef.current(false);
                                    reopenConfirmResolverRef.current = null;
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                            onClick={() => {
                                setReopenConfirm(null);
                                if (reopenConfirmResolverRef.current) {
                                    reopenConfirmResolverRef.current(true);
                                    reopenConfirmResolverRef.current = null;
                                }
                            }}
                        >
                            Re-open
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

const MemoizedNewLeadDataGrid = React.memo(NewLeadDataGrid);
export { MemoizedNewLeadDataGrid as NewLeadDataGrid };

NewLeadDataGrid.propTypes = {
    tableData: PropTypes.array,
    typed: PropTypes.string,
    isReportData: PropTypes.bool,
    selectedRow: PropTypes.object,
    tableTitle: PropTypes.string,
    tablePath: PropTypes.string,
    paginationModel: PropTypes.object,
    setPaginationModel: PropTypes.object,
    handlePaginationChange: PropTypes.func,
    pageCount: PropTypes.number,
    handleChangePerSize: PropTypes.func,
    OpenAssignTask: PropTypes.func,
    reloadCounter: PropTypes.func,
    updateLeadPage: PropTypes.func,
    refreshTable: PropTypes.func,
    refreshCounters: PropTypes.func,
    removeRowOptimistically: PropTypes.func,
    restoreRowOptimistically: PropTypes.func,
    openBulkArchive: PropTypes.func,
    openBulkReminder: PropTypes.object,
    onSelectionModelChange: PropTypes.func,
    forceUpdate: PropTypes.func,
    sortingApiCall: PropTypes.func,
    columnVisibilityModel: PropTypes.object,
    handleColumnVisibilityChange: PropTypes.func,
    handleColumnOrderChange: PropTypes.func,
    openAppointmentAssignTaskFuc: PropTypes.func,
    handleReloadAPI: PropTypes.func,
    setTableData: PropTypes.func,
    handleSetTableData: PropTypes.func,
    setCurrentTreatmentData: PropTypes.func,
    isMultiTreatmentTable: PropTypes.bool,
    setColumnVisibilityModel: PropTypes.func,
    pinProps: PropTypes.object,
    forceLoading: PropTypes.bool,
    showPracticeColumn: PropTypes.bool,
    hasActiveFilters: PropTypes.bool,
    onClearFilters: PropTypes.func,
    isCombinedName: PropTypes.bool,
    handleToggleCombineName: PropTypes.func,
    setIsCombinedName: PropTypes.func,
    isEnforcedByPractice: PropTypes.bool,
    setIsEnforcedByPractice: PropTypes.func,
    activeSubTab: PropTypes.string,
    activeCustomTabSlug: PropTypes.string,
    suppressTaskChecklistColumns: PropTypes.bool,
    customTabOverride: PropTypes.any,
    onBulkCompleteReady: PropTypes.func,
    checklistSwitching: PropTypes.bool,
    onChecklistSwitchingDone: PropTypes.func,
};
